(() => {
  'use strict';

  const focusTrapHandlers = {};
  const gsTrapFocus = (container, elementToFocus = container) => {
    const focusable = Array.from(container?.querySelectorAll(
      'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex^="-"])'
    ) || []).filter((element) => !element.hasAttribute('hidden') && !element.closest('[inert]'));
    if (!focusable.length) return;
    gsRemoveTrapFocus();
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    focusTrapHandlers.keydown = (event) => {
      if (event.key !== 'Tab') return;
      if (event.shiftKey && (event.target === first || event.target === container)) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && event.target === last) {
        event.preventDefault();
        first.focus();
      }
    };
    document.addEventListener('keydown', focusTrapHandlers.keydown);
    (elementToFocus || first).focus?.();
  };
  const gsRemoveTrapFocus = (elementToFocus = null) => {
    if (focusTrapHandlers.keydown) document.removeEventListener('keydown', focusTrapHandlers.keydown);
    focusTrapHandlers.keydown = null;
    elementToFocus?.focus?.();
  };

  const define = (name, constructor) => {
    if (!window.customElements.get(name)) window.customElements.define(name, constructor);
  };
  const delay = (callback, wait = 300) => {
    let timer;
    return (...args) => {
      window.clearTimeout(timer);
      timer = window.setTimeout(() => callback(...args), wait);
    };
  };
  const cartAjaxUrl = (url) => {
    const value = String(url || '').trim();
    if (!value) throw new Error(window.cartStrings?.error || 'Cart request failed');
    const match = value.match(/^([^?#]*)(.*)$/);
    const path = match?.[1] || value;
    const suffix = match?.[2] || '';
    return /\.(?:js|json)$/i.test(path) ? value : `${path}.js${suffix}`;
  };
  const postJson = (url, body) => fetch(cartAjaxUrl(url), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify(body),
  });
  const parseJsonResponse = async (response, fallbackMessage) => {
    const text = await response.text();
    try { return JSON.parse(text); }
    catch (_error) { throw new Error(fallbackMessage || window.cartStrings?.error || 'Cart request failed'); }
  };
  // Only change/update responses contain a complete cart; add responses do not.
  const isFullCart = (state) => Array.isArray(state?.items)
    && Number.isFinite(state.item_count) && Number.isFinite(state.total_price);
  const chargedItemCount = (state) => {
    if (!Array.isArray(state?.items)) return Math.max(0, Number(state?.item_count) || 0);
    return state.items.reduce((count, item) => (
      Number(item?.final_line_price) > 0 ? count + Math.max(0, Number(item?.quantity) || 0) : count
    ), 0);
  };

  const itemProperty = (item, name) => {
    const value = item?.properties?.[name];
    return value == null ? '' : String(value);
  };
  const syncVariantSelects = (root, variant) => {
    if (!root || !variant) return;
    root.querySelectorAll('select').forEach((select, index) => {
      if (variant.options?.[index] != null) select.value = String(variant.options[index]);
    });
  };
  const resolveSelectableVariant = (root, variants, options = {}) => {
    if (!root.querySelector('select')) {
      const id = root.closest('[data-id]')?.dataset.id || root.dataset.id;
      return variants.find((variant) => String(variant.id) === String(id))
        || variants.find((variant) => variant.available) || null;
    }
    const selected = findVariant(variants, selectedValues(root));
    if (selected && (selected.available || !options.skipUnavailable)) return selected;
    if (!selected && !options.skipNonExistent) return null;
    const fallback = variants.find((variant) => variant.available);
    if (fallback) syncVariantSelects(root, fallback);
    return fallback || null;
  };

  const cartErrorMessage = () => window.cartStrings?.error || 'Unable to update your cart. Please check your cart before trying again.';
  const cartError = (message = cartErrorMessage()) => Object.assign(new Error(message), { userMessage: message });
  // A failed POST can still have reached Shopify. Never retry a mutation here.
  const requestJson = async (url, init = {}) => {
    const controller = new AbortController();
    const timeout = window.setTimeout(() => controller.abort(), 15000);
    try {
      const response = await fetch(url, { ...init, headers: { ...init.headers, Accept: 'application/json' }, signal: controller.signal });
      // Shopify's .js endpoints can label JSON as JavaScript. Parse as JSON
      // only (never execute it), while still rejecting HTML/challenge pages.
      if (!/^(?:application\/(?:[\w.+-]*\+)?json|(?:application|text)\/(?:x-)?javascript)(?:\s*;|$)/i.test(response.headers.get('content-type') || '')) throw cartError();
      let data;
      try { data = await response.json(); } catch (_error) { throw cartError(); }
      if (!data || typeof data !== 'object' || Array.isArray(data)) throw cartError();
      if (!response.ok || data.errors || Number(data.status) >= 400) {
        const message = typeof data.description === 'string' ? data.description
          : typeof data.errors === 'string' ? data.errors : cartErrorMessage();
        throw Object.assign(cartError(message), { responseData: data, responseStatus: response.status });
      }
      return data;
    } catch (error) {
      throw error.userMessage ? error : cartError();
    } finally {
      window.clearTimeout(timeout);
    }
  };
  window.GeneralStoreRuntime = { ...window.GeneralStoreRuntime, requestJson };
  let cartChangeQueue = Promise.resolve();
  let cartChangeEpoch = 0;
  const parseJsonScript = (root) => {
    try {
      return JSON.parse(
        root.querySelector('[data-product-variants], script[type="application/json"]')?.textContent || '[]'
      );
    } catch (_error) {
      return [];
    }
  };
  const formatMoney = (cents, format = '${{amount}}') => {
    if (window.Shopify?.formatMoney) return window.Shopify.formatMoney(cents, format);
    const numeric = (Number(cents) || 0) / 100;
    if (String(format).includes('{{')) {
      return String(format)
        .replace(/\{\{\s*amount_no_decimals\s*\}\}/g, Math.round(numeric).toLocaleString('en-US'))
        .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/g, numeric.toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 }))
        .replace(/\{\{\s*amount\s*\}\}/g, numeric.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }));
    }
    return new Intl.NumberFormat(document.documentElement.lang || 'en', {
      style: 'currency',
      currency: document.querySelector('html')?.dataset.currency || 'USD',
    }).format(numeric);
  };
  const selectedValues = (root) => Array.from(root.querySelectorAll('select')).map((select) => select.value);
  const findVariant = (variants, values) => variants.find((variant) =>
    values.every((value, index) => String(variant.options?.[index]) === String(value))
  );
  const dispatch = (target, name, detail = {}) => target.dispatchEvent(new CustomEvent(name, {
    bubbles: true,
    detail,
  }));

  class SearchForm extends HTMLElement {
    constructor() {
      super();
      this.input = this.querySelector('input[type="search"]');
      this.resetButton = this.querySelector('button[type="reset"]');
      if (!this.input) return;
      this.input.form?.addEventListener('reset', (event) => this.onReset(event));
      this.input.addEventListener('input', delay(() => this.onChange(), 250));
      this.onChange();
    }

    onChange() {
      this.resetButton?.classList.toggle('hidden', !this.input.value.length);
    }

    shouldResetForm() {
      return !document.querySelector('[aria-selected="true"] a');
    }

    onFormReset(event) {
      event.preventDefault();
      if (!this.shouldResetForm()) return;
      this.input.value = '';
      this.onChange();
      this.input.focus();
    }

    onReset(event) {
      this.onFormReset(event);
    }
  }
  // main-search.js is a separate deferred asset and extends this constructor.
  // Keep the shared base explicit instead of relying on this bundle's closure.
  window.SearchForm = SearchForm;
  define('search-form', SearchForm);

  class PredictiveSearch extends SearchForm {
    constructor() {
      super();
      if (!this.input) return;
      this.results = this.querySelector('[data-predictive-search]');
      this.status = this.querySelector('.predictive-search-status');
      this.cache = new Map();
      this.controller = null;
      this.input.addEventListener('focus', () => this.input.value.trim() && this.search());
      this.input.addEventListener('input', delay(() => this.search(), 250));
      this.addEventListener('focusout', () => window.setTimeout(() => {
        if (!this.contains(document.activeElement)) this.close();
      }));
      this.addEventListener('keydown', (event) => this.onKeydown(event));
    }

    async search() {
      const term = this.input.value.trim();
      if (!term) return this.close(true);
      if (!this.results || !window.routes?.predictive_search_url) return;
      this.setAttribute('loading', '');
      if (this.status) this.status.textContent = this.dataset.loadingText || 'Loading';
      const key = term.toLocaleLowerCase();
      if (this.cache.has(key)) return this.render(this.cache.get(key));
      this.controller?.abort();
      this.controller = new AbortController();
      try {
        const response = await fetch(`${window.routes.predictive_search_url}?q=${encodeURIComponent(term)}&section_id=predictive-search`, {
          signal: this.controller.signal,
        });
        if (!response.ok) throw new Error(`Predictive search failed: ${response.status}`);
        const html = await response.text();
        const documentFragment = new DOMParser().parseFromString(html, 'text/html');
        const section = documentFragment.querySelector('#shopify-section-predictive-search');
        const markup = section?.innerHTML || '';
        this.cache.set(key, markup);
        this.render(markup);
      } catch (error) {
        if (error.name !== 'AbortError') this.close();
      }
    }

    render(markup) {
      this.results.innerHTML = markup;
      this.removeAttribute('loading');
      this.setAttribute('results', 'true');
      this.setAttribute('open', 'true');
      this.input.setAttribute('aria-expanded', 'true');
      const liveText = this.querySelector('[data-predictive-search-live-region-count-value]')?.textContent;
      if (this.status && liveText) this.status.textContent = liveText;
    }

    close(clear = false) {
      if (clear && this.input) this.input.value = '';
      this.removeAttribute('open');
      this.removeAttribute('loading');
      this.input?.setAttribute('aria-expanded', 'false');
    }

    onKeydown(event) {
      if (event.key === 'Escape') return this.close();
      if (!['ArrowDown', 'ArrowUp', 'Enter'].includes(event.key)) return;
      const options = Array.from(this.querySelectorAll('[role="option"], .predictive-search__item')).filter((item) => !item.hidden);
      if (!options.length) return;
      const active = options.findIndex((item) => item.getAttribute('aria-selected') === 'true');
      if (event.key === 'Enter') {
        const link = options[active]?.querySelector('a') || options[active];
        if (active >= 0 && link) {
          event.preventDefault();
          link.click();
        }
        return;
      }
      event.preventDefault();
      const next = event.key === 'ArrowDown'
        ? (active + 1) % options.length
        : (active <= 0 ? options.length - 1 : active - 1);
      options.forEach((item, index) => item.setAttribute('aria-selected', String(index === next)));
      options[next].scrollIntoView({ block: 'nearest' });
    }
  }
  define('predictive-search', PredictiveSearch);

  class QuantityInput extends HTMLElement {
    connectedCallback() {
      if (this.dataset.runtimeBound === 'true') return;
      this.dataset.runtimeBound = 'true';
      this.input = this.querySelector('input[type="number"]');
      if (!this.input) return;
      this.querySelectorAll('button[name="plus"], button[name="minus"]').forEach((button) => {
        button.addEventListener('click', (event) => {
          event.preventDefault();
          const previous = this.input.value;
          if (button.name === 'plus') this.input.stepUp();
          else this.input.stepDown();
          if (this.input.value !== previous) this.input.dispatchEvent(new Event('change', { bubbles: true }));
          this.validate();
        });
      });
      this.input.addEventListener('change', () => this.validate());
      this.validate();
    }

    validate() {
      const value = Number(this.input?.value);
      const minus = this.querySelector('button[name="minus"]');
      const plus = this.querySelector('button[name="plus"]');
      if (minus && this.input?.min) minus.classList.toggle('disabled', value <= Number(this.input.min));
      if (plus && this.input?.max) plus.classList.toggle('disabled', value >= Number(this.input.max));
    }
  }
  define('quantity-input', QuantityInput);

  class CartRemoveButton extends HTMLElement {
    connectedCallback() {
      if (this.dataset.runtimeBound) return;
      this.dataset.runtimeBound = 'true';
      this.addEventListener('click', (event) => {
        event.preventDefault();
        this.closest('cart-items, cart-drawer-items')?.updateQuantity(this.dataset.index, 0, event);
      });
    }
  }
  define('cart-remove-button', CartRemoveButton);

  class CartItems extends HTMLElement {
    connectedCallback() {
      if (this.onQuantityChange) return;
      this.onQuantityChange = (event) => {
        const input = event.target.closest('input[name="updates[]"], input[data-index]');
        if (!input) return;
        window.clearTimeout(this.changeTimer);
        this.changeTimer = window.setTimeout(() => {
          if (input.isConnected) this.updateQuantity(input.dataset.index, Number(input.value), event);
        }, 300);
      };
      this.addEventListener('change', this.onQuantityChange);
      this.onQuantityKeydown = (event) => {
        if (event.key !== 'Enter') return;
        const input = event.target.closest('input[name="updates[]"], input[data-index]');
        if (!input) return;
        event.preventDefault();
        input.dispatchEvent(new Event('change', { bubbles: true }));
      };
      this.addEventListener('keydown', this.onQuantityKeydown);
      if (typeof window.subscribe === 'function' && window.PUB_SUB_EVENTS?.cartUpdate) {
        this.cartUpdateUnsubscribe = window.subscribe(window.PUB_SUB_EVENTS.cartUpdate, (event) => {
          // Our own changes render every mounted cart surface below. Other
          // writers (PDP, quick add, apps) need a fresh read, never another POST.
          if (['cart-items', 'cart-drawer-upsells', 'cart-drawer-upsell'].includes(event?.source)) return;
          if (document.querySelector('cart-drawer') && !document.querySelector('cart-items')) return;
          const owner = document.querySelector('cart-items') || document.querySelector('cart-drawer-items');
          if (owner !== this) return;
          const job = cartChangeQueue.then(() => {
            const current = document.querySelector('cart-items') || document.querySelector('cart-drawer-items');
            return current?.recoverCart(current.getSectionsToRender());
          });
          cartChangeQueue = job.catch(() => {});
          return job;
        });
      }
    }

    disconnectedCallback() {
      window.clearTimeout(this.changeTimer);
      this.removeEventListener('change', this.onQuantityChange);
      this.removeEventListener('keydown', this.onQuantityKeydown);
      this.onQuantityChange = null;
      this.onQuantityKeydown = null;
      this.cartUpdateUnsubscribe?.();
      this.cartUpdateUnsubscribe = null;
    }

    getSectionsToRender() {
      // One write updates all mounted surfaces, not just the one clicked.
      // At most five sections (Shopify bundled-section limit).
      return [
        ...(document.getElementById('CartDrawer') ? [{ id: 'CartDrawer', section: 'cart-drawer', selector: '.drawer__inner' }] : []),
        { id: 'main-cart-items', section: document.getElementById('main-cart-items')?.dataset.id, selector: '.js-contents' },
        ...(document.getElementById('cart-live-region-text') ? [{ id: 'cart-live-region-text', section: 'cart-live-region-text', selector: '.shopify-section' }] : []),
        { id: 'main-cart-footer', section: document.getElementById('main-cart-footer')?.dataset.id, selector: '.js-contents' },
      ].filter((item) => item.section);
    }

    findQuantityInput(key, line) {
      return Array.from(this.querySelectorAll('input[name="updates[]"][data-index]')).find((input) =>
        key ? input.dataset.cartItemKey === key : input.dataset.index === String(line)
      );
    }

    validQuantity(input, quantity) {
      const minimum = Number(input?.min || 0);
      const maximum = input?.max ? Number(input.max) : Infinity;
      const step = Number(input?.step || 1);
      return Number.isSafeInteger(quantity) && quantity >= 0 && (quantity === 0 || (
        quantity >= minimum && quantity <= maximum && step > 0 && (quantity - minimum) % step === 0
      ));
    }

    updateQuantity(line, quantity, event) {
      const input = this.findQuantityInput(null, line);
      const key = input?.dataset.cartItemKey;
      const amount = Number(quantity);
      if (!key || !this.validQuantity(input, amount)) {
        if (input) input.value = input.getAttribute('value');
        this.setLiveMessage(line, cartErrorMessage());
        return Promise.resolve();
      }
      const epoch = cartChangeEpoch;
      // Capture identity before rendering can renumber rows or replace this element.
      const task = () => {
        if (epoch !== cartChangeEpoch) return;
        const owner = this.isConnected ? this : document.querySelector(this.tagName.toLowerCase());
        const currentInput = owner?.findQuantityInput(key);
        if (!currentInput || !owner.validQuantity(currentInput, amount)) {
          owner?.setLiveMessage(line, cartErrorMessage());
          return;
        }
        return owner.changeQuantity(key, currentInput.dataset.index, amount, event);
      };
      const drawer = document.querySelector('cart-drawer');
      const job = drawer?.enqueueCartMutation ? drawer.enqueueCartMutation(task) : cartChangeQueue.then(task);
      cartChangeQueue = job.catch(() => {});
      return cartChangeQueue;
    }

    async changeQuantity(key, line, quantity, event) {
      const sections = this.getSectionsToRender();
      const focusWasInside = this.contains(document.activeElement);
      const focusId = focusWasInside ? document.activeElement?.id : null;
      this.setLoading(line, true);
      try {
        const state = await requestJson(window.routes.cart_change_url, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ id: key, quantity, sections: sections.map((item) => item.section), sections_url: window.location.pathname + window.location.search }),
        });
        if (!Array.isArray(state.items) || !Number.isSafeInteger(state.item_count)) throw cartError();
        this.renderCartState(state, sections);
        this.setLiveMessage(line, '');
        const current = state.items.find((item) => item.key === key);
        if (quantity > 0 && current && current.quantity !== quantity) {
          this.setLiveMessage(line, (window.cartStrings?.quantityError || 'Only [quantity] available.').replace('[quantity]', current.quantity));
        }
        this.notifyCartUpdate(state, line, quantity);
      } catch (error) {
        cartChangeEpoch += 1;
        // Reconcile using GET only. Unknown server outcome must not double an order.
        const recovered = await this.recoverCart(sections);
        const message = error.userMessage || cartErrorMessage();
        this.setLiveMessage(line, message);
        const errors = document.getElementById('cart-errors') || document.getElementById('CartDrawer-CartErrors');
        if (errors) {
          errors.textContent = message;
          errors.setAttribute('role', 'alert');
          if (!recovered) {
            const link = document.createElement('a');
            link.href = window.routes.cart_url;
            link.textContent = ' View cart';
            errors.append(link);
          }
        }
      } finally {
        this.setLoading(line, false);
        const owner = document.querySelector(this.tagName.toLowerCase());
        owner?.setLoading(line, false);
        if (focusWasInside && (!document.activeElement || document.activeElement === document.body || !document.activeElement.isConnected)) {
          const target = owner?.findQuantityInput(key) || (focusId && document.getElementById(focusId))
            || document.querySelector('cart-drawer .drawer__close, #checkout');
          target?.focus();
        }
      }
    }

    renderCartState(state, sections) {
      // Shopify can return HTTP 200 with a null section: do not treat that as rendered.
      if (!sections.every((section) => typeof state.sections?.[section.section] === 'string' && state.sections[section.section].trim())) throw cartError();
      this.classList.toggle('is-empty', state.item_count === 0);
      document.querySelector('cart-items')?.classList.toggle('is-empty', state.item_count === 0);
      const drawer = document.querySelector('cart-drawer');
      drawer?.classList.toggle('is-empty', state.item_count === 0);
      drawer?.updateHorizonCartBubble?.(chargedItemCount(state));
      drawer?.syncEmptyState?.();
      document.getElementById('main-cart-footer')?.classList.toggle('is-empty', state.item_count === 0);
      for (const section of sections) {
        if (!this.replaceSection(section, state.sections[section.section])) throw cartError();
      }
      drawer?.prepareNestedUpsells(state);
      drawer?.syncEmptyState();
      document.querySelector('cart-drawer')?.bindControls?.();
      document.querySelector('cart-drawer')?.bindTrigger?.();
    }

    notifyCartUpdate(state, line, quantity) {
      try {
        if (typeof window.publish === 'function' && window.PUB_SUB_EVENTS?.cartUpdate) {
          Promise.resolve(window.publish(window.PUB_SUB_EVENTS.cartUpdate, { source: 'cart-items', cartData: state }))
            .catch((error) => console.error('Cart subscriber failed', error));
        }
        const owner = this.isConnected ? this : document.querySelector(this.tagName.toLowerCase());
        dispatch(document, 'cart:updated', { cart: state, line, quantity });
      } catch (error) { console.error('Cart subscriber failed', error); }
    }

    async recoverCart(sections) {
      try {
        const url = new URL(window.routes.cart_url, window.location.origin);
        const state = await requestJson(`${url.pathname}.js`, { cache: 'no-store' });
        if (!Array.isArray(state.items) || !Number.isSafeInteger(state.item_count)) return false;
        url.searchParams.set('sections', sections.map((section) => section.section).join(','));
        state.sections = await requestJson(url.href, { cache: 'no-store' });
        this.renderCartState(state, sections);
        this.notifyCartUpdate(state);
        return true;
      } catch (_error) { return false; }
    }

    replaceSection(section, html) {
      if (!html) return false;
      const parsed = new DOMParser().parseFromString(html, 'text/html');
      if (section.section === 'cart-icon-bubble') {
        const sourceBubble = parsed.querySelector('#cart-icon-bubble');
        const targetBubble = document.querySelector('#cart-icon-bubble');
        if (sourceBubble && targetBubble) {
          targetBubble.replaceWith(sourceBubble);
          document.querySelector('cart-drawer')?.bindTrigger?.();
          return true;
        }
        return false;
      }
      const source = parsed.querySelector(section.selector);
      const host = document.getElementById(section.id);
      // The subtotal block is optional. Never insert an entire section wrapper
      // into the footer when a merchant deliberately removed that block.
      if (section.id === 'main-cart-footer' && !source && !host?.querySelector(section.selector)) {
        return Boolean(host && parsed.getElementById('main-cart-footer'));
      }
      const target = host?.querySelector(section.selector) || host;
      if (!source || !target) return false;
      if (section.id === 'CartDrawer') document.querySelector('cart-drawer')?.preserveUpsellState(source, target);
      target.innerHTML = source.innerHTML;
      return true;
    }

    setLiveMessage(line, message) {
      const lineError = document.querySelector(`#Line-item-error-${line} .cart-item__error-text, #CartDrawer-LineItemError-${line} .cart-item__error-text`);
      if (lineError) lineError.textContent = message;
      const live = document.getElementById('cart-live-region-text') || document.getElementById('CartDrawer-LiveRegionText');
      if (live) {
        live.textContent = message;
        live.setAttribute('aria-hidden', String(!message));
      }
    }

    setLoading(line, loading) {
      this.classList.toggle('cart__items--disabled', loading);
      this.querySelectorAll(`#CartItem-${line} .loading__spinner, #CartDrawer-Item-${line} .loading__spinner, #CartItem-${line} .loading-overlay__spinner, #CartDrawer-Item-${line} .loading-overlay__spinner`).forEach((spinner) => {
        spinner.classList.toggle('hidden', !loading);
      });
    }
  }
  define('cart-items', CartItems);
  define('cart-drawer-items', class CartDrawerItems extends CartItems {});

  class CartDiscountField extends HTMLElement {
    connectedCallback() {
      if (this.dataset.runtimeBound) return;
      this.dataset.runtimeBound = 'true';
      this.form = this.querySelector('.cart-discount-form');
      this.input = this.querySelector('input[name="discount"]');
      this.onSubmit = (event) => this.apply(event);
      this.onInput = () => this.clearMessage();
      this.onClick = (event) => {
        const button = event.target.closest('[data-discount-remove]');
        if (!button || !this.contains(button)) return;
        this.remove(button.dataset.discountRemove, event);
      };
      this.form?.addEventListener('submit', this.onSubmit);
      this.input?.addEventListener('input', this.onInput);
      this.addEventListener('click', this.onClick);
    }

    disconnectedCallback() {
      this.form?.removeEventListener('submit', this.onSubmit);
      this.input?.removeEventListener('input', this.onInput);
      this.removeEventListener('click', this.onClick);
    }

    codes() {
      return Array.from(document.querySelectorAll('cart-discount-field [data-discount-code]'))
        .map((item) => item.dataset.discountCode?.trim())
        .filter((code, index, codes) => code && codes.indexOf(code) === index);
    }

    message(type, text) {
      const current = document.getElementById(this.id) || this;
      const error = current.querySelector('[data-discount-error]');
      const status = current.querySelector('[data-discount-status]');
      if (error) {
        error.textContent = type === 'error' ? text : '';
        error.hidden = type !== 'error';
      }
      if (status) status.textContent = type === 'status' ? text : '';
      current.classList.toggle('cart-discount--error', type === 'error');
    }

    clearMessage() {
      this.message('', '');
      this.input?.removeAttribute('aria-invalid');
    }

    setBusy(busy) {
      const current = document.getElementById(this.id) || this;
      current.toggleAttribute('aria-busy', busy);
      current.querySelectorAll('button, input').forEach((control) => { control.disabled = busy; });
      current.querySelector('[data-discount-button-label]')?.classList.toggle('hidden', busy);
      current.querySelector('[data-discount-submit] .loading-overlay__spinner')?.classList.toggle('hidden', !busy);
    }

    apply(event) {
      event.preventDefault();
      event.stopPropagation();
      const code = this.input?.value.trim();
      if (!code) {
        this.input?.setAttribute('aria-invalid', 'true');
        this.message('error', this.dataset.emptyError || 'Please enter a discount code!');
        this.input?.focus();
        return;
      }
      const existing = this.codes();
      if (existing.some((item) => item.toLocaleUpperCase() === code.toLocaleUpperCase())) {
        this.input.value = '';
        this.message('status', `${code} is already applied.`);
        return;
      }
      this.queueUpdate([...existing, code], { action: 'apply', code });
    }

    remove(code, event) {
      event?.preventDefault();
      event?.stopPropagation();
      if (!code) return;
      const remaining = this.codes().filter((item) => item.toLocaleUpperCase() !== code.toLocaleUpperCase());
      this.queueUpdate(remaining, { action: 'remove', code });
    }

    queueUpdate(codes, operation) {
      const componentId = this.id;
      const task = () => document.getElementById(componentId)?.updateDiscounts(codes, operation);
      const drawer = document.querySelector('cart-drawer');
      const job = drawer?.enqueueCartMutation ? drawer.enqueueCartMutation(task) : cartChangeQueue.then(task);
      cartChangeQueue = job.catch(() => {});
      return job;
    }

    async updateDiscounts(codes, operation) {
      const owner = document.querySelector('cart-items') || document.querySelector('cart-drawer-items');
      if (!owner?.getSectionsToRender || !window.routes?.cart_update_url) {
        this.message('error', this.dataset.networkError || 'The discount could not be updated. Please try again.');
        return;
      }
      const sections = owner.getSectionsToRender();
      this.setBusy(true);
      this.clearMessage();
      try {
        const sectionPayload = {
          sections: sections.map((item) => item.section),
          sections_url: window.location.pathname + window.location.search,
        };
        const update = (discount, renderSections) => requestJson(window.routes.cart_update_url, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ discount, ...(renderSections ? sectionPayload : {}) }),
        });
        const replaceDiscounts = async (nextCodes) => {
          // Shopify keeps inapplicable codes in `discount_codes`. Clear first so
          // the submitted list is a deterministic replacement, not an append.
          if (!nextCodes.length) return update('', true);
          await update('', false);
          return update(nextCodes.join(','), true);
        };

        let state = operation.action === 'remove'
          ? await replaceDiscounts(codes)
          : await update(codes.join(','), true);
        if (!Array.isArray(state.items) || !Number.isSafeInteger(state.item_count) || !Array.isArray(state.discount_codes)) throw cartError();

        if (operation.action === 'apply') {
          const result = state.discount_codes.find((item) =>
            String(item.code).toLocaleUpperCase() === operation.code.toLocaleUpperCase()
          );
          if (!result || result.applicable === false) {
            const previousCodes = codes.filter((item) => item.toLocaleUpperCase() !== operation.code.toLocaleUpperCase());
            state = await replaceDiscounts(previousCodes);
            if (!Array.isArray(state.items) || !Number.isSafeInteger(state.item_count) || !Array.isArray(state.discount_codes)) throw cartError();
            owner.renderCartState(state, sections);
            this.refreshFields(state.sections);
            owner.notifyCartUpdate(state);

            const current = document.getElementById(this.id) || this;
            const input = current.querySelector('input[name="discount"]');
            if (input) {
              input.value = '';
              input.setAttribute('aria-invalid', 'true');
            }
            current.message('error', current.dataset.invalidError || 'This discount code is invalid or not applicable.');
            input?.focus();
            return;
          }

          const intended = codes.map((item) => item.toLocaleUpperCase());
          const hasResidualCode = state.discount_codes.some((item) =>
            item.applicable === false || !intended.includes(String(item.code).toLocaleUpperCase())
          );
          if (hasResidualCode) {
            state = await replaceDiscounts(codes);
            if (!Array.isArray(state.items) || !Number.isSafeInteger(state.item_count) || !Array.isArray(state.discount_codes)) throw cartError();
            const sanitized = state.discount_codes.every((item) =>
              item.applicable !== false && intended.includes(String(item.code).toLocaleUpperCase())
            );
            if (!sanitized) throw cartError();
          }
        }

        owner.renderCartState(state, sections);
        this.refreshFields(state.sections);
        owner.notifyCartUpdate(state);

        const current = document.getElementById(this.id);
        const rendered = Array.from(current?.querySelectorAll('[data-discount-code]') || []).some((item) =>
          item.dataset.discountCode?.toLocaleUpperCase() === operation.code.toLocaleUpperCase()
        );
        if (operation.action === 'apply' && !rendered) {
          current?.message('status', current.dataset.shippingMessage || 'Shipping discounts are calculated at checkout.');
        } else {
          current?.message('status', operation.action === 'apply' ? `${operation.code} applied.` : `${operation.code} removed.`);
        }
        const focusTarget = current?.querySelector('input[name="discount"]');
        if (focusTarget) {
          focusTarget.value = '';
          focusTarget.focus();
        }
      } catch (error) {
        const recovered = await owner.recoverCart(sections);
        const current = document.getElementById(this.id) || this;
        current.message('error', current.dataset.networkError || 'The discount could not be updated. Please try again.');
        if (!recovered) dispatch(current, 'cart:error', { message: error.userMessage || cartErrorMessage() });
      } finally {
        (document.getElementById(this.id) || this).setBusy(false);
      }
    }

    refreshFields(sectionHtml) {
      const sources = new Map();
      Object.values(sectionHtml || {}).forEach((html) => {
        if (typeof html !== 'string') return;
        const parsed = new DOMParser().parseFromString(html, 'text/html');
        parsed.querySelectorAll('cart-discount-field[id]').forEach((field) => sources.set(field.id, field));
      });
      document.querySelectorAll('cart-discount-field[id]').forEach((field) => {
        const source = sources.get(field.id);
        if (source && !field.isEqualNode(source)) field.replaceWith(document.importNode(source, true));
      });
    }
  }
  define('cart-discount-field', CartDiscountField);

  class CartNote extends HTMLElement {
    connectedCallback() {
      if (this.dataset.runtimeBound) return;
      this.dataset.runtimeBound = 'true';
      this.addEventListener('input', () => {
        this.dataset.dirty = 'true';
        clearTimeout(this.noteTimer);
        this.noteTimer = setTimeout(() => this.flush().catch(error => {
          const drawer = document.querySelector('cart-drawer');
          dispatch(drawer || this, 'cart:error', {message: error.message});
        }), 300);
      });
    }

    async flush() {
      clearTimeout(this.noteTimer);
      if (this.savePromise) await this.savePromise;
      if (this.dataset.dirty !== 'true') return;
      const value = this.querySelector('textarea')?.value || '';
      const drawer = this.closest('cart-drawer') || document.querySelector('cart-drawer');
      const task = async () => {
        const response = await postJson(window.routes.cart_update_url, {note: value});
        const result = await parseJsonResponse(response, 'Cart note could not be saved.');
        if (!response.ok || result.status || result.errors) throw new Error('Cart note could not be saved.');
        for (const note of new Set([this, ...document.querySelectorAll('cart-note')])) {
          if (note.querySelector('textarea')?.value === value) delete note.dataset.dirty;
        }
      };
      this.savePromise = drawer?.enqueueCartMutation ? drawer.enqueueCartMutation(task) : task();
      try { await this.savePromise; } finally { this.savePromise = null; }
    }

    disconnectedCallback() {
      clearTimeout(this.noteTimer);
    }
  }
  define('cart-note', CartNote);

  class CartDrawer extends HTMLElement {
    connectedCallback() {
      if (this.dataset.runtimeBound) {
        document.addEventListener('keydown', this.onDocumentKeydown);
        document.addEventListener('shopify:block:select', this.onEditorBlockSelect);
        document.addEventListener('cart:updated', this.onCartUpdated);
        document.addEventListener('shopify:cart:lines-update', this.onShopifyCartUpdate);
        this.addEventListener('cart:error', this.onCartError);
        this.bindControls();
        this.bindTrigger();
        return;
      }
      this.dataset.runtimeBound = 'true';
      this.cartReadPromise = null;
      this.lastKnownCart = null;
      this.cartReadBlockedUntil = 0;
      this.addEventListener('keyup', (event) => event.key === 'Escape' && this.close());
      this.onDocumentKeydown = (event) => {
        if (event.key === 'Escape' && this.classList.contains('active')) {
          event.preventDefault();
          this.close();
        }
      };
      document.addEventListener('keydown', this.onDocumentKeydown);
      this.addEventListener('submit', (event) => this.onCheckoutSubmit(event));
      this.onCartUpdated = (event) => {
        if (event.detail?.source === 'cart-drawer-upsells') return;
        window.queueMicrotask(() => this.syncPreselectedUpsells());
      };
      this.onCartError = (event) => this.showCartError(event.detail?.message);
      document.addEventListener('cart:updated', this.onCartUpdated);
      this.onShopifyCartUpdate = (event) => {
        const revision = this.refreshRevision = (this.refreshRevision || 0) + 1;
        Promise.resolve(event.promise).then(async ({ detail, userErrors } = {}) => {
          if (detail?.source === 'general-store-cart') return;
          if (detail?.drawerRendered) return;
          if (revision !== this.refreshRevision) return;
          if (detail?.didError || userErrors?.length) throw new Error('Unable to add this item. Please check your cart.');
          let sections = detail?.sections;
          if (!this.validDrawerSection(sections)) sections = await this.fetchDrawerSections();
          if (revision !== this.refreshRevision || !this.isConnected) return;
          this.renderContents({ sections }, { open: event.action === 'add' });
        }).catch((error) => {
          if (revision === this.refreshRevision) this.showRefreshError(error.message);
        });
      };
      document.addEventListener('shopify:cart:lines-update', this.onShopifyCartUpdate);
      this.addEventListener('cart:error', this.onCartError);
      this.onEditorBlockSelect = (event) => {
        if (!window.Shopify?.designMode) return;
        const selected = event.target;
        const upsells = selected?.closest?.('cart-drawer-upsells')
          || selected?.querySelector?.('cart-drawer-upsells');
        if (!upsells || !this.contains(upsells)) return;
        window.setTimeout(() => this.open(), 0);
      };
      document.addEventListener('shopify:block:select', this.onEditorBlockSelect);
      this.bindControls();
      this.bindTrigger();
      this.syncEmptyState();
      window.queueMicrotask(() => this.syncPreselectedUpsells());
    }

    syncEmptyState() {
      const empty = this.classList.contains('is-empty');
      this.querySelectorAll('.drawer__header, .cart-drawer__body, .drawer__footer').forEach((region) => {
        region.toggleAttribute('inert', empty);
        region.setAttribute('aria-hidden', String(empty));
      });
      this.querySelectorAll('.drawer__inner-empty').forEach((region) => {
        region.toggleAttribute('inert', !empty);
        region.setAttribute('aria-hidden', String(!empty));
      });
    }

    bindControls() {
      this.querySelectorAll('[data-prepare-express-checkout]').forEach((button) => {
        if (button.dataset.expressBound) return;
        button.dataset.expressBound = 'true';
        button.addEventListener('click', async () => {
          button.disabled = true;
          try {
            await this.prepareCheckout();
            if (this.classList.contains('is-empty')) throw new Error('Your cart is empty.');
            this.querySelectorAll('[data-cart-express-wallets]').forEach((wallets) => {
              wallets.hidden = false;
              wallets.inert = false;
            });
            this.querySelectorAll('[data-prepare-express-checkout]').forEach((trigger) => {
              trigger.hidden = true;
            });
          } catch (error) {
            this.showCartError(error.message);
          } finally {
            button.disabled = false;
          }
        });
      });
      const overlay = this.querySelector('#CartDrawer-Overlay');
      if (overlay && overlay.dataset.cartDrawerBound !== 'true') {
        overlay.dataset.cartDrawerBound = 'true';
        overlay.addEventListener('click', () => this.close());
      }

      this.querySelectorAll('.drawer__close, [data-cart-drawer-close]').forEach((button) => {
        if (button.dataset.cartDrawerBound === 'true') return;
        button.dataset.cartDrawerBound = 'true';
        button.addEventListener('click', () => this.close());
      });
    }

    bindTrigger() {
      const icon = document.getElementById('cart-icon-bubble')
        || document.querySelector('cart-icon.header-actions__cart-icon, .header-actions__cart-icon');
      // The visible/clickable hit area is the wrapping link in this theme.
      // Binding only the inner bubble leaves most clicks navigating to /cart.
      const trigger = icon?.closest('a, button') || icon;
      if (trigger && trigger.dataset.cartDrawerBound !== 'true') {
        trigger.dataset.cartDrawerBound = 'true';
        trigger.setAttribute('aria-haspopup', 'dialog');
        trigger.addEventListener('click', (event) => {
          event.preventDefault();
          document.querySelector('cart-drawer')?.refreshAndOpen(trigger);
        }, true);
      }
    }

    validDrawerSection(sections) {
      const html = sections?.['cart-drawer'];
      if (typeof html !== 'string' || !html.trim()) return false;
      return Boolean(new DOMParser().parseFromString(html, 'text/html').querySelector('#CartDrawer .drawer__inner'));
    }

    async fetchDrawerSections() {
      const url = new URL(window.location.href);
      url.searchParams.delete('section_id');
      url.searchParams.set('sections', 'cart-drawer');
      const response = await fetch(url, { credentials: 'same-origin', cache: 'no-store' });
      if (!response.ok) throw new Error('Unable to refresh your cart. Please try again.');
      const sections = await response.json();
      if (!this.validDrawerSection(sections)) throw new Error('Unable to load your cart. Please try again.');
      return sections;
    }

    async refreshAndOpen(trigger) {
      const revision = this.refreshRevision = (this.refreshRevision || 0) + 1;
      this.activeElement = trigger || document.activeElement;
      try {
        const sections = await this.fetchDrawerSections();
        if (revision !== this.refreshRevision || !this.isConnected) return;
        this.renderContents({ sections });
      } catch (error) {
        if (revision === this.refreshRevision) this.showRefreshError(error.message);
      }
    }

    showRefreshError(message) {
      let alert = document.getElementById('cart-refresh-error');
      if (!alert) {
        alert = document.createElement('div');
        alert.id = 'cart-refresh-error';
        alert.setAttribute('role', 'alert');
        alert.style.cssText = 'position:fixed;bottom:20px;right:20px;z-index:10001;background:white;color:#222;padding:16px;max-width:340px;box-shadow:0 2px 12px #0003';
        document.body.append(alert);
      }
      alert.textContent = message || 'Unable to refresh your cart.';
      const link = document.createElement('a');
      link.href = window.routes.cart_url;
      link.textContent = ' View cart';
      alert.append(link);
    }

    open(trigger) {
      if (this.classList.contains('active')) return;
      this.activeElement = trigger || document.activeElement;
      this.syncEmptyState();
      this.classList.add('animate', 'active');
      document.querySelectorAll('[aria-controls="CartDrawer"]').forEach(node => node.setAttribute('aria-expanded', 'true'));
      document.body.classList.add('overflow-hidden');

      const focusContainer = this.classList.contains('is-empty')
        ? this.querySelector('.drawer__inner-empty')
        : this.querySelector('#CartDrawer');
      const focusElement = this.classList.contains('is-empty')
        ? focusContainer?.querySelector('.drawer__close, a[href], button:not([disabled])')
        : this.querySelector('.drawer__inner');
      const transitionElement = this.querySelector('.drawer__inner') || focusElement;

      let focusActivated = false;
      const activateFocusTrap = () => {
        if (focusActivated || !this.classList.contains('active') || !focusContainer || !focusElement) return;
        focusActivated = true;
        window.clearTimeout(this.focusFallbackTimer);
        gsTrapFocus(focusContainer, focusElement);
      };

      // The drawer stays visibility:hidden during its opening transition.
      // Focus only after that transition, with a fallback for reduced motion
      // and browsers that omit transitionend.
      transitionElement?.addEventListener('transitionend', activateFocusTrap, { once: true });
      this.focusFallbackTimer = window.setTimeout(activateFocusTrap, 450);
      dispatch(this, 'cart-drawer:open');
    }

    close() {
      window.clearTimeout(this.focusFallbackTimer);
      this.classList.remove('active');
      document.querySelectorAll('[aria-controls="CartDrawer"]').forEach(node => node.setAttribute('aria-expanded', 'false'));
      document.body.classList.remove('overflow-hidden');
      gsRemoveTrapFocus(this.activeElement);
      dispatch(this, 'cart-drawer:close');
    }

    getSectionsToRender() {
      return [
        { id: 'cart-drawer', section: 'cart-drawer', selector: '#CartDrawer' },
        { id: 'cart-icon-bubble', section: 'cart-icon-bubble', selector: '.shopify-section' },
      ];
    }

    setActiveElement(element) {
      this.activeElement = element;
    }

    preserveUpsellState(source, target) {
      const oldNote = target.querySelector('cart-note[data-dirty="true"]');
      const newNote = source.querySelector('cart-note');
      if (oldNote && newNote) {
        const field = newNote.querySelector('textarea');
        if (field) field.textContent = oldNote.querySelector('textarea')?.value || '';
        newNote.dataset.dirty = 'true';
      }
      source.querySelectorAll('cart-drawer-gift').forEach(replacement => {
        const current = Array.from(target.querySelectorAll('cart-drawer-gift'))
          .find(gift => gift.dataset.giftKey === replacement.dataset.giftKey);
        if (!current || window.Shopify?.designMode) return;
        // Keep a shopper-selected variant only while it still belongs to this gift.
        if (parseJsonScript(replacement).some(v => String(v.id) === current.dataset.id)) {
          replacement.dataset.id = current.dataset.id;
          const selects = current.querySelectorAll('select');
          replacement.querySelectorAll('select').forEach((select, index) => {
            if (selects[index]) select.value = selects[index].value;
          });
        }
      });
      const currentByIdentity = new Map();
      target.querySelectorAll('cart-drawer-upsell[data-handle]').forEach((upsell) => {
        const identity = upsell.dataset.upsellKey || upsell.dataset.handle;
        if (!identity) return;
        const matches = currentByIdentity.get(identity) || [];
        matches.push(upsell);
        currentByIdentity.set(identity, matches);
      });

      source.querySelectorAll('cart-drawer-upsell[data-handle]').forEach((replacement) => {
        const identity = replacement.dataset.upsellKey || replacement.dataset.handle;
        const matches = currentByIdentity.get(identity);
        const current = matches?.shift();
        if (!current) return;

        const currentConfig = this.upsellConfiguration(current);
        const replacementConfig = this.upsellConfiguration(replacement);
        if (currentConfig !== replacementConfig) {
          replacement.dataset.selectionHydrated = 'false';
          replacement.dataset.selectionConfigChanged = 'true';
          return;
        }

        // Unchanged configuration preserves the shopper's explicit choice.
        const selected = current.dataset.selected === 'true';
        replacement.dataset.selected = String(selected);
        replacement.dataset.selectionHydrated = current.dataset.selectionHydrated || 'false';
        if (current.dataset.selectionConfigChanged === 'true') {
          replacement.dataset.selectionConfigChanged = 'true';
        }
        replacement.querySelectorAll('.upsell-toggle-btn').forEach((button) => {
          button.setAttribute('aria-pressed', String(selected));
        });

        if (current.dataset.id) replacement.dataset.id = current.dataset.id;
        const replacementInput = replacement.querySelector('product-form input[name="id"]');
        const currentInput = current.querySelector('product-form input[name="id"]');
        if (replacementInput && currentInput) replacementInput.value = currentInput.value;

        const currentSelects = Array.from(current.querySelectorAll('.upsell__variant-picker select'));
        replacement.querySelectorAll('.upsell__variant-picker select').forEach((select, index) => {
          if (currentSelects[index]) select.value = currentSelects[index].value;
        });
      });
    }

    renderContents(state, options = {}) {
      if (!this.validDrawerSection(state.sections)) throw new Error('Unable to load your cart. Please try again.');
      document.getElementById('cart-refresh-error')?.remove();
      const drawerHtml = state.sections?.['cart-drawer'];
      const bubbleHtml = state.sections?.['cart-icon-bubble'];
      const parser = new DOMParser();
      if (drawerHtml) {
        const source = parser.parseFromString(drawerHtml, 'text/html').querySelector('#CartDrawer');
        const target = this.querySelector('#CartDrawer');
        if (source && target) {
          this.preserveUpsellState(source, target);
          target.innerHTML = source.innerHTML;
          target.dataset.chargedCount = source.dataset.chargedCount;
          this.updateHorizonCartBubble(source.dataset.chargedCount);
          this.classList.toggle('is-empty', Boolean(source.querySelector('.cart-drawer__empty-content')));
          this.bindControls();
          this.syncEmptyState();
          if (this.dataset.upsellBusy === 'true') this.setUpsellBusy(true);
        }
      }
      if (bubbleHtml) {
        const sourceDocument = parser.parseFromString(bubbleHtml, 'text/html');
        const source = sourceDocument.querySelector('#cart-icon-bubble') || sourceDocument.querySelector('.shopify-section');
        const target = document.querySelector('#cart-icon-bubble');
        if (source && target) target.innerHTML = source.innerHTML;
      }
      if (state.item_count !== undefined) this.updateHorizonCartBubble(chargedItemCount(state));
      if (options.open !== false) this.open();
      if (options.reconcile !== false) window.queueMicrotask(() => this.syncPreselectedUpsells());
    }

    updateHorizonCartBubble(itemCount) {
      const count = Math.max(0, Number(itemCount) || 0);
      document.querySelectorAll('.cart-bubble__text-count').forEach((node) => {
        node.textContent = count ? String(Math.min(count, 99)) : '';
        node.classList.toggle('hidden', count === 0);
      });
      document.querySelectorAll('.cart-bubble').forEach((node) => node.classList.toggle('visually-hidden', count === 0));
      document.querySelectorAll('cart-icon.header-actions__cart-icon').forEach((node) => {
        node.classList.toggle('header-actions__cart-icon--has-cart', count > 0);
      });
      try {
        sessionStorage.setItem('cart-count', JSON.stringify({ value: String(count), timestamp: Date.now() }));
      } catch (_) { /* Cart rendering must work when storage is unavailable. */ }
    }

    getToggleUpsells() {
      return Array.from(this.querySelectorAll('cart-drawer-upsell[data-toggle="true"][data-product-id][data-id]'));
    }

    setUpsellBusy(busy) {
      if (busy) this.paymentCartRevision = (this.paymentCartRevision || 0) + 1;
      document.querySelectorAll('cart-wide-express-checkout').forEach((component) => {
        if (busy) component.invalidate?.();
        else component.scheduleRefresh?.();
      });
      if (busy) {
        this.querySelectorAll('[data-cart-express-wallets]').forEach((wallets) => {
          wallets.hidden = true;
          wallets.inert = true;
        });
        this.querySelectorAll('[data-prepare-express-checkout]').forEach((trigger) => {
          trigger.hidden = false;
        });
      }
      if (busy) this.dataset.upsellBusy = 'true';
      else delete this.dataset.upsellBusy;
      const controls = new Set([
        ...this.getToggleUpsells().map((upsell) => upsell.querySelector('.upsell-toggle-btn')).filter(Boolean),
      ]);
      controls.forEach((control) => this.setRuntimeControlBusy(control, busy));
    }

    setRuntimeControlBusy(control, busy) {
      if (busy) {
        if (control.dataset.cartRuntimeBusy !== 'true') {
          control.dataset.cartRuntimeBusy = 'true';
          control.dataset.cartRuntimePreviousAria = control.hasAttribute('aria-disabled')
            ? control.getAttribute('aria-disabled')
            : '__missing__';
          if ('disabled' in control) control.dataset.cartRuntimePreviousDisabled = String(Boolean(control.disabled));
          else control.dataset.cartRuntimePreviousInert = String(Boolean(control.inert));
        }
        control.setAttribute('aria-disabled', 'true');
        if ('disabled' in control) control.disabled = true;
        else control.inert = true;
        return;
      }

      if (control.dataset.cartRuntimeBusy !== 'true') return;
      const previousAria = control.dataset.cartRuntimePreviousAria;
      if (previousAria === '__missing__') control.removeAttribute('aria-disabled');
      else control.setAttribute('aria-disabled', previousAria || 'false');
      if ('disabled' in control) control.disabled = control.dataset.cartRuntimePreviousDisabled === 'true';
      else control.inert = control.dataset.cartRuntimePreviousInert === 'true';
      delete control.dataset.cartRuntimeBusy;
      delete control.dataset.cartRuntimePreviousAria;
      delete control.dataset.cartRuntimePreviousDisabled;
      delete control.dataset.cartRuntimePreviousInert;
    }

    showCartError(message = '') {
      const target = this.querySelector('#CartDrawer-CartErrors, .cart-errors, [data-cart-errors]');
      if (!target) return;
      target.textContent = message || window.cartStrings?.error || 'There was an error updating your cart. Please try again.';
      target.hidden = false;
      target.setAttribute('role', 'alert');
    }

    enqueueCartMutation(task) {
      if (typeof task !== 'function') return Promise.resolve({ changed: false, cart: null });
      this.setUpsellBusy(true);
      const previous = this.cartMutationQueue || Promise.resolve();
      const tracked = previous.catch(() => undefined).then(() => task());
      this.cartMutationQueue = tracked;
      this.cartMutationPromise = tracked;
      const clear = () => {
        if (this.cartMutationQueue === tracked) {
          this.cartMutationQueue = null;
          this.cartMutationPromise = null;
          this.setUpsellBusy(false);
        }
      };
      tracked.then(clear, clear);
      return tracked;
    }

    enqueueUpsellMutation(task) {
      return this.enqueueCartMutation(task);
    }

    findUpsell(identity = {}) {
      const key = typeof identity === 'string' ? identity : identity.key;
      const candidates = this.getToggleUpsells();
      if (key) {
        const keyed = candidates.find((upsell) => upsell.dataset.upsellKey === key);
        if (keyed) return keyed;
      }
      return candidates.find((upsell) => (
        upsell.dataset.handle === identity.handle
        && Number(upsell.dataset.productId) === Number(identity.productId)
        && Number(upsell.dataset.id) === Number(identity.variantId)
      )) || null;
    }

    async waitForCartMutations() {
      let pending = this.cartMutationPromise;
      let result = null;
      while (pending) {
        result = await pending;
        if (result?.error) throw result.error;
        if (pending === this.cartMutationPromise) break;
        pending = this.cartMutationPromise;
      }
      return result;
    }

    waitForUpsellMutations() {
      return this.waitForCartMutations();
    }

    findCartUpsellItems(cartItems, identity = {}) {
      const key = typeof identity === 'string' ? identity : identity.key;
      if (!key) return [];
      return cartItems.filter((item) => itemProperty(item, '_theme_upsell_key') === String(key)
        || itemProperty(item, '_horizon_cart_upsell') === String(key));
    }

    isDecorativeCartItem(item) {
      return Boolean(
        item.parent_relationship?.parent_key
        || itemProperty(item, '_theme_upsell_key')
        || itemProperty(item, '_horizon_cart_upsell')
        || itemProperty(item, '_Auto Added') === 'Yes'
        || itemProperty(item, '_theme_gift_key')
        || itemProperty(item, '_theme_additional_key')
      );
    }

    hasPrimaryCartItem(cart) {
      return (cart?.items || []).some((item) => !this.isDecorativeCartItem(item));
    }

    findInvalidAdditionalItem(cart) {
      const items = cart?.items || [];
      if (!this.hasPrimaryCartItem(cart)) return items.find((item) => this.isDecorativeCartItem(item)) || null;
      return items.find((item) => {
        if (!itemProperty(item, '_theme_additional_key')) return false;
        const parentProductId = Number(itemProperty(item, '_theme_parent_product_id'));
        const minimum = Math.max(1, Number(itemProperty(item, '_theme_min_parent_quantity')) || 1);
        if (!parentProductId) return false;
        const parentQuantity = items.reduce((sum, candidate) => {
          if (this.isDecorativeCartItem(candidate)) return sum;
          return Number(candidate.product_id) === parentProductId
            ? sum + Number(candidate.quantity || 0)
            : sum;
        }, 0);
        return parentQuantity < minimum;
      }) || null;
    }

    async removeInvalidAdditionalItems(cart) {
      let state = cart;
      let changed = false;
      let invalid = this.findInvalidAdditionalItem(state);
      let attemptsRemaining = Math.max(2, (state?.items?.length || 0) + 1);
      while (invalid && attemptsRemaining > 0) {
        const response = await postJson(window.routes?.cart_change_url || '/cart/change.js', {
          id: invalid.key,
          quantity: 0,
          sections: ['cart-drawer', 'cart-icon-bubble'],
          sections_url: window.location.pathname + window.location.search,
        });
        const result = await parseJsonResponse(response, 'Unable to remove an ineligible additional item');
        if (!response.ok || result.status || result.errors) {
          throw new Error(result.description || result.message || 'Unable to remove an ineligible additional item');
        }
        if (result.sections) this.renderContents(result, { open: false, reconcile: false });
        state = isFullCart(result) ? result : await this.readCart();
        changed = true;
        attemptsRemaining -= 1;
        invalid = this.findInvalidAdditionalItem(state);
      }
      if (invalid) throw new Error('Additional item eligibility could not be synchronized');
      return { changed, cart: state };
    }

    getUpsellDecisionKey(cart, upsell) {
      const themeId = this.dataset.themeId || 'theme';
      return `theme-upsell-choice-v2:${themeId}:${upsell.dataset.upsellKey || upsell.dataset.handle || ''}:${this.upsellConfiguration(upsell)}`;
    }

    upsellConfiguration(upsell) {
      return JSON.stringify([
        upsell.dataset.upsellKey || '',
        upsell.dataset.productId || '',
        upsell.dataset.defaultSelected || 'false',
        upsell.dataset.style || '',
        upsell.dataset.toggle || '',
      ]);
    }

    readUpsellDecision(cart, upsell) {
      const key = this.getUpsellDecisionKey(cart, upsell);
      if (window.__cartUpsellChoices?.has(key)) return window.__cartUpsellChoices.get(key);
      try {
        return window.sessionStorage.getItem(key);
      } catch (_error) {
        return null;
      }
    }

    persistUpsellDecision(cart, upsell, selected) {
      const key = this.getUpsellDecisionKey(cart, upsell);
      window.__cartUpsellChoices ||= new Map();
      window.__cartUpsellChoices.set(key, String(Boolean(selected)));
      try {
        window.sessionStorage.setItem(key, String(Boolean(selected)));
      } catch (_error) {}
    }

    observeEditorUpsellConfiguration(upsell) {
      // This key deliberately excludes the configuration: remember the LAST
      // setting, not a historical decision for each on/off setting separately.
      // It survives Shopify replacing the entire section or preview document.
      const key = `theme-upsell-config-v2:${this.dataset.themeId || 'theme'}:${upsell.dataset.upsellKey}`;
      const config = this.upsellConfiguration(upsell);
      window.__cartUpsellEditorConfigs ||= new Map();
      let previous = window.__cartUpsellEditorConfigs.get(key);
      try {
        if (previous === undefined) previous = sessionStorage.getItem(key);
        sessionStorage.setItem(key, config);
      } catch (_) { /* In-memory tracking still supports section reloads. */ }
      window.__cartUpsellEditorConfigs.set(key, config);
      if (previous !== config) {
        upsell.dataset.selectionConfigChanged = 'true';
        upsell.dataset.selectionHydrated = 'false';
        upsell.setSelected?.(upsell.dataset.defaultSelected === 'true');
      }
    }

    hydrateUpsellSelections(cart) {
      const hasPrimaryItem = this.hasPrimaryCartItem(cart);
      this.getToggleUpsells().forEach((upsell) => {
        this.observeEditorUpsellConfiguration(upsell);
        // Hydrating another/new block must not overwrite a customer's current choice.
        if (hasPrimaryItem && upsell.dataset.selectionHydrated === 'true' && upsell.dataset.selectionConfigChanged !== 'true') return;
        const saved = this.readUpsellDecision(cart, upsell);
        const configurationChanged = upsell.dataset.selectionConfigChanged === 'true';
        let selected = configurationChanged
          ? upsell.dataset.defaultSelected === 'true'
          : saved !== null ? saved === 'true'
            : upsell.dataset.defaultSelected === 'true';
        if (configurationChanged || saved === null) this.persistUpsellDecision(cart, upsell, selected);
        delete upsell.dataset.selectionConfigChanged;
        if (!hasPrimaryItem) selected = false;
        upsell.setSelected?.(selected);
        // An empty cart is ineligible, not an opt-out. Apply the saved choice or
        // merchant preselection once a primary item arrives.
        upsell.dataset.selectionHydrated = String(hasPrimaryItem);
      });
    }

    nestedParentSignature(item) {
      return JSON.stringify([
        String(item.variant_id || item.id),
        Object.entries(item.properties || {}).sort(([a], [b]) => a.localeCompare(b)),
        String(item.selling_plan_allocation?.selling_plan?.id || ''),
      ]);
    }

    nestedBindingKey(cart, upsell) {
      return 'theme-nested-upsell:' + String(cart?.token || 'cart').split('?')[0]
        + ':' + upsell.dataset.upsellKey;
    }

    readNestedBinding(cart, upsell) {
      const key = this.nestedBindingKey(cart, upsell);
      this.nestedBindings ||= new Map();
      if (this.nestedBindings.has(key)) return this.nestedBindings.get(key);
      try {
        const binding = JSON.parse(window.sessionStorage.getItem(key) || 'null');
        this.nestedBindings.set(key, binding);
        return binding;
      } catch (_error) { return null; }
    }

    saveNestedBinding(cart, upsell, binding) {
      const key = this.nestedBindingKey(cart, upsell);
      this.nestedBindings ||= new Map();
      this.nestedBindings.set(key, binding);
      try {
        if (binding) window.sessionStorage.setItem(key, JSON.stringify(binding));
        else window.sessionStorage.removeItem(key);
      } catch (_error) {}
    }

    eligibleNestedParent(item, variantId) {
      return Boolean(item?.key) && !this.isDecorativeCartItem(item)
        && Number(item.variant_id || item.id) !== Number(variantId)
        && !Array.from(this.querySelectorAll('cart-drawer-gift[data-id]')).some((gift) =>
          Number(gift.dataset.id) === Number(item.variant_id || item.id))
        && !item.has_components && !item.item_components?.length;
    }

    findNestedParent(cart, upsell) {
      const items = cart?.items || [];
      const binding = this.readNestedBinding(cart, upsell);
      if (binding) {
        // Discount recalculation can change a line key without changing its identity.
        const parent = items.find((item) => this.eligibleNestedParent(item, upsell.dataset.id)
          && (item.key === binding.key || this.nestedParentSignature(item) === binding.signature));
        if (parent) return parent;
        this.saveNestedBinding(cart, upsell, null);
      }
      return items.find((item) => this.eligibleNestedParent(item, upsell.dataset.id)) || null;
    }

    prepareNestedUpsells(cart) {
      this.getToggleUpsells().forEach((upsell) => {
        const nested = this.findCartUpsellItems(cart.items || [], upsell.dataset.upsellKey)
          .find((item) => item.parent_relationship?.parent_key);
        if (nested) {
          const parent = cart.items.find((item) => item.key === nested.parent_relationship.parent_key);
          if (parent) this.saveNestedBinding(cart, upsell, {
            key: parent.key, signature: this.nestedParentSignature(parent),
            defaultSelected: upsell.dataset.defaultSelected,
          });
        }
        const binding = this.readNestedBinding(cart, upsell);
        if (!binding) return;
        // A deliberate editor setting change starts a new selection; a section
        // refresh after deleting the parent must not resurrect its children.
        if (window.Shopify?.designMode && binding.defaultSelected !== upsell.dataset.defaultSelected) {
          this.saveNestedBinding(cart, upsell, null);
          return;
        }
        if (!this.findNestedParent(cart, upsell)) {
          upsell.setSelected(false);
          upsell.dataset.selectionHydrated = 'false';
          this.saveNestedBinding(cart, upsell, null);
        }
      });
    }

    buildNestedUpsellItem(upsell, cart) {
      const parent = this.findNestedParent(cart, upsell);
      if (!parent) throw new Error('Add an eligible main product before selecting this add-on.');
      const item = {
        id: Number(upsell.dataset.id), quantity: 1,
        parent_line_key: parent.key,
        properties: { _theme_upsell_key: upsell.dataset.upsellKey || '' },
      };
      const sellingPlan = upsell.querySelector('input[name="selling_plan"]:not(:disabled)')?.value;
      if (sellingPlan) item.selling_plan = Number(sellingPlan);
      return item;
    }

    findUpsellMismatch(cart) {
      this.prepareNestedUpsells(cart);
      const cartItems = Array.isArray(cart?.items) ? cart.items : [];
      const hasPrimaryItem = this.hasPrimaryCartItem(cart);
      for (const upsell of this.getToggleUpsells()) {
        const variantId = Number(upsell.dataset.id);
        const markedItems = this.findCartUpsellItems(cartItems, upsell.dataset.upsellKey || '');
        const desiredItem = markedItems.find((item) => Number(item.variant_id || item.id) === variantId);
        const staleItem = markedItems.find((item) => Number(item.variant_id || item.id) !== variantId);
        const selected = hasPrimaryItem && upsell.dataset.selected === 'true';
        // Remove an old variant before adding its replacement. Its recorded
        // parent survives this operation, even if other main products were added.
        if (selected && staleItem) return { action: 'remove', upsell, cart, cartItem: staleItem };
        if (selected && !desiredItem) {
          if (!this.findNestedParent(cart, upsell)) {
            upsell.setSelected(false);
            continue;
          }
          return { action: 'add', upsell, cart };
        }
        if (selected && desiredItem && Number(desiredItem.quantity || 0) !== 1) {
          return { action: 'change', upsell, cart, cartItem: desiredItem, quantity: 1 };
        }
        if (!selected && markedItems[0]) return { action: 'remove', upsell, cart, cartItem: markedItems[0] };
      }
      return null;
    }

    async applyUpsellMismatch(mismatch) {
      const sections = ['cart-drawer', 'cart-icon-bubble'];
      const line = mismatch.action === 'add'
        ? 0
        : mismatch.cart.items.findIndex((item) => item === mismatch.cartItem) + 1;
      const payload = mismatch.action === 'add'
        ? {
            items: [this.buildNestedUpsellItem(mismatch.upsell, mismatch.cart)],
            sections,
            sections_url: window.location.pathname + window.location.search,
          }
        : {
            line,
            quantity: mismatch.action === 'change' ? Number(mismatch.quantity || 1) : 0,
            sections,
            sections_url: window.location.pathname + window.location.search,
          };
      if (mismatch.action !== 'add' && !payload.line) {
        throw new Error('Unable to identify the selected cart upsell');
      }
      const route = mismatch.action === 'add'
        ? (window.routes?.cart_add_url || '/cart/add.js')
        : (window.routes?.cart_change_url || '/cart/change.js');
      const response = await postJson(route, payload);
      const state = await parseJsonResponse(response, 'Unable to synchronize cart upsell');
      if (!response.ok || state.status) {
        throw new Error(state.description || state.message || 'Unable to synchronize cart upsell');
      }
      if (mismatch.action === 'add') {
        const parent = mismatch.cart.items.find((item) => item.key === payload.items[0].parent_line_key);
        this.saveNestedBinding(mismatch.cart, mismatch.upsell, {
          key: parent.key, signature: this.nestedParentSignature(parent),
          defaultSelected: mismatch.upsell.dataset.defaultSelected,
        });
      }
      if (state.sections) this.renderContents(state, { open: false, reconcile: false });
      return mismatch.action !== 'add' && isFullCart(state) ? state : this.readCart();
    }

    async readCart() {
      if (this.cartReadPromise) return this.cartReadPromise;
      if (Date.now() < this.cartReadBlockedUntil) throw new Error('Please wait a moment before updating your cart again.');
      const cartRoute = window.routes?.cart_url || '/cart';
      this.cartReadPromise = (async () => {
        const response = await fetch(`${cartRoute.replace(/\/$/, '')}.js`, { headers: { Accept: 'application/json' } });
        if (response.status === 429) {
          this.cartReadBlockedUntil = Date.now() + 1500;
          throw new Error('Please wait a moment before updating your cart again.');
        }
        const cart = await parseJsonResponse(response, 'Unable to verify cart');
        if (!response.ok || cart.status) throw new Error(cart.description || cart.message || 'Unable to verify cart');
        this.lastKnownCart = cart;
        this.cartReadBlockedUntil = 0;
        return cart;
      })();
      try { return await this.cartReadPromise; } finally { this.cartReadPromise = null; }
    }

    async reconcileUpsells() {
      let cart = await this.readCart();
      const additionalResult = await this.removeInvalidAdditionalItems(cart);
      cart = additionalResult.cart;
      const giftKeys = Array.from(this.querySelectorAll('cart-drawer-gift')).map(gift => gift.dataset.giftKey);
      for (const key of giftKeys) {
        const gift = Array.from(this.querySelectorAll('cart-drawer-gift')).find(item => item.dataset.giftKey === key);
        const result = await gift?.reconcileGift(cart);
        if (result) {
          cart = result.cart;
          additionalResult.changed ||= result.changed;
        }
      }
      if (window.Shopify?.designMode || !this.hasPrimaryCartItem(cart)
        || this.getToggleUpsells().some((upsell) => upsell.dataset.selectionHydrated !== 'true')) {
        this.hydrateUpsellSelections(cart);
      }
      let changed = additionalResult.changed;
      let attemptsRemaining = Math.max(1, this.getToggleUpsells().length * 4 + 1);
      let mismatch = this.findUpsellMismatch(cart);

      while (mismatch && attemptsRemaining > 0) {
        cart = await this.applyUpsellMismatch(mismatch);
        changed = true;
        attemptsRemaining -= 1;
        mismatch = this.findUpsellMismatch(cart);
      }

      if (mismatch) throw new Error('Cart upsell state could not be synchronized');
      if (changed) {
        this.updateHorizonCartBubble(chargedItemCount(cart));
        if (typeof window.publish === 'function' && window.PUB_SUB_EVENTS?.cartUpdate) {
          window.publish(window.PUB_SUB_EVENTS.cartUpdate, {
            source: 'cart-drawer-upsells',
            cartData: cart,
          });
        }
        dispatch(this, 'cart:updated', { cart, source: 'cart-drawer-upsells' });
      }
      return { changed, cart };
    }

    syncPreselectedUpsells() {
      if (this.upsellSyncPromise) return this.upsellSyncPromise;
      if (!this.getToggleUpsells().length && !this.querySelector('cart-drawer-gift')
        && !document.querySelector('product-info-upsell')) {
        return Promise.resolve({ changed: false, cart: this.lastKnownCart });
      }

      const syncTask = async () => {
        try {
          return await this.reconcileUpsells();
        } catch (error) {
          console.error(error);
          dispatch(this, 'cart:error', { message: error.message });
          return { changed: false, cart: null, error };
        }
      };
      this.upsellSyncPromise = this.enqueueCartMutation(syncTask);
      const clearSync = () => {
        this.upsellSyncPromise = null;
      };
      this.upsellSyncPromise.then(clearSync, clearSync);
      return this.upsellSyncPromise;
    }

    setCheckoutProgress(busy) {
      this.querySelectorAll('button[name="checkout"], input[name="checkout"][type="submit"]').forEach((button) => {
        if (busy) {
          button.setAttribute('aria-busy', 'true');
          if (button.tagName === 'BUTTON' && !button.querySelector('[data-checkout-progress]')) {
            const spinner = document.createElement('span');
            spinner.dataset.checkoutProgress = 'true';
            spinner.setAttribute('aria-hidden', 'true');
            spinner.innerHTML = '<svg width="18" height="18" viewBox="0 0 24 24" style="vertical-align:middle;margin-inline-end:8px"><circle cx="12" cy="12" r="9" fill="none" stroke="currentColor" stroke-width="3" stroke-dasharray="42 16"><animateTransform attributeName="transform" type="rotate" from="0 12 12" to="360 12 12" dur=".8s" repeatCount="indefinite"/></circle></svg>';
            button.prepend(spinner);
          }
        } else {
          button.removeAttribute('aria-busy');
          button.querySelector('[data-checkout-progress]')?.remove();
        }
      });
    }

    async prepareCheckout() {
      const checkoutSelection = new Map(this.getToggleUpsells().map((upsell) => [
        upsell.dataset.upsellKey,
        upsell.dataset.selected === 'true',
      ]));
      await this.waitForCartMutations();
      for (const note of this.querySelectorAll('cart-note')) await note.flush?.();
      await this.waitForCartMutations();
      const cart = await this.readCart();
      this.getToggleUpsells().forEach((upsell) => {
        if (!checkoutSelection.has(upsell.dataset.upsellKey)) return;
        const selected = checkoutSelection.get(upsell.dataset.upsellKey);
        upsell.setSelected?.(selected);
        upsell.dataset.selectionHydrated = 'true';
        this.persistUpsellDecision(cart, upsell, selected);
      });
      const result = await this.syncPreselectedUpsells();
      if (result?.error) throw result.error;
      await this.waitForCartMutations();
      return result;
    }

    async onCheckoutSubmit(event) {
      const form = event.target.closest('form');
      const checkoutButton = event.submitter?.name === 'checkout'
        ? event.submitter
        : Array.from(this.querySelectorAll('[name="checkout"]')).find((button) => button.form === form);
      if (!form || !checkoutButton || this.dataset.upsellCheckoutCommit === 'true') return;

      event.preventDefault();
      if (this.dataset.upsellCheckoutRunning === 'true') return;
      this.dataset.upsellCheckoutRunning = 'true';
      this.setCheckoutProgress(true);
      const progressObserver = new MutationObserver(() => this.setCheckoutProgress(true));
      progressObserver.observe(this, { childList: true, subtree: true });

      try {
        await this.prepareCheckout();
        // Updates can replace the original form while checkout is waiting.
        const currentButton = Array.from(this.querySelectorAll('[name="checkout"]'))
          .find((button) => button.form?.id === form.id);
        const currentForm = currentButton?.form || (form.isConnected ? form : null);
        if (!currentForm) throw new Error('Cart updated. Please try checkout again.');
        if (this.classList.contains('is-empty') || currentButton?.disabled) {
          throw new Error('Your cart is empty or not ready for checkout.');
        }
        this.dataset.upsellCheckoutCommit = 'true';
        const checkoutInput = document.createElement('input');
        checkoutInput.type = 'hidden';
        checkoutInput.name = 'checkout';
        checkoutInput.value = checkoutButton.value || 'Checkout';
        checkoutInput.dataset.upsellCheckoutCommit = 'true';
        currentForm.append(checkoutInput);
        HTMLFormElement.prototype.submit.call(currentForm);
      } catch (error) {
        delete this.dataset.upsellCheckoutCommit;
        delete this.dataset.upsellCheckoutRunning;
        progressObserver.disconnect();
        this.setCheckoutProgress(false);
        dispatch(this, 'cart:error', { message: error.message });
      } finally {
        progressObserver.disconnect();
      }
    }

    disconnectedCallback() {
      document.removeEventListener('cart:updated', this.onCartUpdated);
      document.removeEventListener('shopify:cart:lines-update', this.onShopifyCartUpdate);
      document.removeEventListener('keydown', this.onDocumentKeydown);
      document.removeEventListener('shopify:block:select', this.onEditorBlockSelect);
      this.removeEventListener('cart:error', this.onCartError);
      window.clearTimeout(this.focusFallbackTimer);
      if (this.classList.contains('active')) this.close();
    }
  }
  define('cart-drawer', CartDrawer);

  // Official cart wallets use the entire server-side cart, not the surrounding
  // product form. Never intercept or synthesize a click inside a payment wallet.
  class CartWideExpressCheckout extends HTMLElement {
    static refreshing = false;
    connectedCallback() {
      this.querySelector('[data-express-retry]')?.addEventListener('click', () => this.scheduleRefresh());
      this.scheduleRefresh();
    }

    disconnectedCallback() { clearTimeout(this.refreshTimer); }

    invalidate() {
      const content = this.querySelector('[data-express-content]');
      if (content) content.inert = true;
      const status = this.querySelector('[data-express-status]');
      if (status) {
        status.hidden = false;
        status.textContent = 'Updating cart checkout…';
      }
    }

    scheduleRefresh() {
      if (CartWideExpressCheckout.refreshing || !this.isConnected) return;
      clearTimeout(this.refreshTimer);
      this.refreshTimer = setTimeout(() => this.refresh(), 150);
    }

    async refresh() {
      if (CartWideExpressCheckout.refreshing || !this.isConnected) return;
      CartWideExpressCheckout.refreshing = true;
      document.querySelectorAll('cart-wide-express-checkout').forEach(node => node.invalidate());
      const content = this.querySelector('[data-express-content]');
      const status = this.querySelector('[data-express-status]');
      const retry = this.querySelector('[data-express-retry]');
      retry.hidden = true;
      let stale = false;
      try {
        const drawer = document.querySelector('cart-drawer');
        if (!drawer?.prepareCheckout) throw new Error('Cart is not ready. Please try again.');
        await drawer.prepareCheckout();
        const revision = drawer.paymentCartRevision;
        const url = new URL(location.href);
        url.searchParams.delete('section_id');
        url.searchParams.set('sections', 'cart-express-checkout');
        const response = await fetch(url, { credentials: 'same-origin', cache: 'no-store' });
        if (!response.ok) throw new Error('Unable to load express checkout. Please retry.');
        const sections = await response.json();
        const html = sections['cart-express-checkout'];
        if (typeof html !== 'string') throw new Error('Express checkout could not be loaded.');
        const root = new DOMParser().parseFromString(html, 'text/html').querySelector('[data-cart-express-response]');
        if (!root) throw new Error('Express checkout could not be loaded.');
        if (revision !== drawer.paymentCartRevision || drawer.cartMutationPromise) {
          stale = true;
          return;
        }
        document.querySelectorAll('cart-wide-express-checkout').forEach(node => {
          const target = node.querySelector('[data-express-content]');
          target.innerHTML = root.innerHTML;
          target.inert = false;
          node.querySelector('[data-express-status]').hidden = true;
          node.querySelector('[data-express-retry]').hidden = true;
        });
      } catch (error) {
        document.querySelectorAll('cart-wide-express-checkout').forEach(node => {
          node.querySelector('[data-express-status]').textContent = error.message;
          node.querySelector('[data-express-retry]').hidden = false;
        });
      } finally {
        CartWideExpressCheckout.refreshing = false;
        if (stale) document.querySelector('cart-wide-express-checkout')?.scheduleRefresh();
      }
    }
  }
  define('cart-wide-express-checkout', CartWideExpressCheckout);


  class CartDrawerUpsells extends HTMLElement {
    connectedCallback() {
      if (this.dataset.runtimeBound) return;
      this.dataset.runtimeBound = 'true';
      window.queueMicrotask(() => {
        this.placeInConfiguredRegion();
      });
    }

    placeInConfiguredRegion() {
      const drawer = this.closest('cart-drawer') || document.querySelector('cart-drawer');
      if (!drawer) return;
      const selector = this.dataset.position === 'footer' ? '.drawer__footer' : '.cart-drawer__body';
      const target = drawer.querySelector(selector);
      if (target && !target.contains(this)) target.append(this);
    }

  }
  define('cart-drawer-upsells', CartDrawerUpsells);

  class CartDrawerUpsell extends HTMLElement {
    connectedCallback() {
      if (this.dataset.runtimeBound) return;
      this.dataset.runtimeBound = 'true';
      this.variants = parseJsonScript(this);
      if (this.dataset.toggle === 'false') {
        this.addEventListener('submit', (event) => {
          event.preventDefault();
          event.stopImmediatePropagation();
          this.addNestedItem();
        }, true);
      }
      this.toggleButton = this.querySelector('.upsell-toggle-btn');
      this.variantInput = this.querySelector('product-form input[name="id"]');

      this.querySelectorAll('.upsell__variant-picker select').forEach((select) => {
        select.addEventListener('change', () => this.updateVariant({ reconcileCart: true }));
      });
      const requestToggle = () => {
        const drawer = this.closest('cart-drawer') || document.querySelector('cart-drawer');
        const identity = this.getIdentity();
        const desiredSelected = this.dataset.selected !== 'true';
        // Record intent before any network work or section replacement.
        drawer?.persistUpsellDecision(drawer.lastKnownCart, this, desiredSelected);
        this.dataset.selectionHydrated = 'true';
        delete this.dataset.selectionConfigChanged;
        this.setSelected(desiredSelected);
        drawer?.enqueueUpsellMutation(() => {
          const current = drawer.findUpsell(identity) || this;
          return current.toggle(desiredSelected);
        });
      };
      if (this.toggleButton?.tagName !== 'BUTTON') {
        this.toggleButton?.setAttribute('role', 'button');
        this.toggleButton?.setAttribute('tabindex', '0');
        this.toggleButton?.addEventListener('keydown', (event) => {
          if (!['Enter', ' '].includes(event.key) || event.target.closest('select, option, input, a, product-form')) return;
          event.preventDefault();
          requestToggle();
        });
      }
      this.toggleButton?.addEventListener('click', (event) => {
        if (event.target.closest('select, option, input, a, product-form')) return;
        event.preventDefault();
        requestToggle();
      });

      this.setSelected(this.dataset.selected === 'true');
      this.updateVariant();
      {
        const drawer = this.closest('cart-drawer');
        drawer?.observeEditorUpsellConfiguration(this);
        // A block-only editor reload does not reconnect the drawer itself.
        window.queueMicrotask(() => {
          if (this.isConnected) drawer?.syncPreselectedUpsells();
        });
      }
    }

    updateVariant(options = {}) {
      if (!this.variants.length) return;
      const picker = this.querySelector('.upsell__variant-picker') || this;
      const variant = resolveSelectableVariant(picker, this.variants, {
        skipUnavailable: this.dataset.skipUnavailable === 'true',
        skipNonExistent: this.dataset.skipNonExistent === 'true',
      });
      if (!variant || !variant.available) {
        this.dataset.variantAvailable = 'false';
        this.setSelected(false);
        this.toggleButton?.setAttribute('aria-disabled', 'true');
        if (this.toggleButton?.tagName === 'BUTTON') this.toggleButton.disabled = true;
        if (options.reconcileCart) {
          const drawer = this.closest('cart-drawer') || document.querySelector('cart-drawer');
          drawer?.syncPreselectedUpsells();
        }
        return;
      }
      const previousVariantId = Number(this.dataset.id);
      this.dataset.id = String(variant.id);
      this.dataset.variantAvailable = 'true';
      this.toggleButton?.removeAttribute('aria-disabled');
      if (this.toggleButton?.tagName === 'BUTTON') this.toggleButton.disabled = false;
      if (this.variantInput) this.variantInput.value = String(variant.id);

      if (this.dataset.updatePrices === 'true') {
        const price = Math.max(0, Number(variant.price || 0) * Number(this.dataset.percentageLeft || 1) - Number(this.dataset.fixedDiscount || 0));
        const comparePrice = Number(variant.compare_at_price) > Number(variant.price)
          ? Number(variant.compare_at_price)
          : Number(variant.price || 0);
        this.querySelector('.regular-price')?.replaceChildren(formatMoney(price, this.dataset.moneyFormat));
        const compare = this.querySelector('.compare-price');
        if (compare) {
          compare.replaceChildren(formatMoney(comparePrice, this.dataset.moneyFormat));
          compare.classList.toggle('hidden', comparePrice <= price);
        }
      }

      if (
        options.reconcileCart
        && this.dataset.selected === 'true'
        && previousVariantId
        && previousVariantId !== Number(variant.id)
      ) {
        const drawer = this.closest('cart-drawer') || document.querySelector('cart-drawer');
        drawer?.syncPreselectedUpsells();
      }
    }

    setSelected(selected) {
      this.dataset.selected = String(selected);
      this.classList.toggle('upsell--selected', selected);
      this.toggleButton?.setAttribute('aria-pressed', String(selected));
    }

    async addNestedItem() {
      const drawer = this.closest('cart-drawer') || document.querySelector('cart-drawer');
      if (!drawer || this.dataset.loading === 'true') return;
      this.dataset.loading = 'true';
      const button = this.querySelector('.upsell__add-btn');
      button?.classList.add('loading');
      try {
        await drawer.enqueueCartMutation(async () => {
          const cart = await drawer.readCart();
          const response = await postJson(window.routes?.cart_add_url || '/cart/add.js', {
            items: [drawer.buildNestedUpsellItem(this, cart)],
            sections: ['cart-drawer', 'cart-icon-bubble'],
            sections_url: window.location.pathname + window.location.search,
          });
          const state = await parseJsonResponse(response, 'Unable to add this item.');
          if (!response.ok || state.status || state.errors) {
            throw new Error(state.description || state.message || 'Unable to add this item.');
          }
          drawer.renderContents(state);
          return { changed: true };
        });
      } catch (error) {
        drawer.showCartError(error.message);
      } finally {
        this.dataset.loading = 'false';
        button?.classList.remove('loading');
      }
    }

    getIdentity() {
      return {
        key: this.dataset.upsellKey || '',
        handle: this.dataset.handle || '',
        productId: Number(this.dataset.productId),
        variantId: Number(this.dataset.id),
      };
    }

    async toggle(desiredSelected = this.dataset.selected !== 'true') {
      if (this.dataset.loading === 'true') return { changed: false, cart: null };
      if (this.dataset.variantAvailable === 'false') return { changed: false, cart: null };
      this.dataset.loading = 'true';
      const wasSelected = this.dataset.selected === 'true';
      const drawer = this.closest('cart-drawer') || document.querySelector('cart-drawer');
      const identity = this.getIdentity();
      const wasHydrated = this.dataset.selectionHydrated === 'true';
      // Explicit input takes priority even if initial hydration is still pending.
      this.dataset.selectionHydrated = 'true';
      delete this.dataset.selectionConfigChanged;
      this.setSelected(desiredSelected);

      try {
        if (!drawer) throw new Error('Cart drawer is unavailable');
        drawer.persistUpsellDecision(drawer.lastKnownCart, this, desiredSelected);
        if (desiredSelected) drawer.saveNestedBinding(drawer.lastKnownCart, this, null);
        const result = await drawer.reconcileUpsells();
        const verifiedCart = result.cart || await drawer.readCart();
        const verifiedSelected = drawer.findCartUpsellItems(verifiedCart.items || [], identity).some((item) => (
          Number(item.variant_id || item.id) === identity.variantId
        ));
        if (verifiedSelected !== desiredSelected) throw new Error('Cart upsell state could not be verified');

        const current = drawer.findUpsell(identity);
        current?.setSelected(desiredSelected);
        if (current) drawer.persistUpsellDecision(verifiedCart, current, desiredSelected);
        if (verifiedCart.item_count !== undefined) drawer.classList.toggle('is-empty', verifiedCart.item_count === 0);
        // reconcileUpsells already published the verified cart once.
        return { changed: result.changed, cart: verifiedCart };
      } catch (error) {
        const current = drawer?.findUpsell(identity) || this;
        current.setSelected(desiredSelected);
        current.dataset.selectionHydrated = 'true';
        console.error(error);
        dispatch(current, 'cart:error', { message: error.message });
        return { changed: false, error };
      } finally {
        const current = drawer?.findUpsell(identity) || this;
        current.dataset.loading = 'false';
      }
    }
  }
  define('cart-drawer-upsell', CartDrawerUpsell);

  class ProductInfoUpsell extends HTMLElement {
    connectedCallback() {
      if (this.dataset.runtimeBound) return;
      this.dataset.runtimeBound = 'true';
      this.variants = parseJsonScript(this);
      this.toggleButton = this.querySelector('.upsell-toggle-btn');
      this.variantInput = this.querySelector('product-form input[name="id"]');

      this.querySelectorAll('.upsell__variant-picker select').forEach((select) => {
        select.addEventListener('change', () => this.updateVariant());
      });
      if (this.dataset.toggle === 'true' && this.toggleButton) {
        this.toggleButton.addEventListener('click', (event) => {
          if (event.target.closest('select, option, input, a, product-form')) return;
          event.preventDefault();
          this.setSelected(this.dataset.selected !== 'true');
        });
      }

      this.setSelected(this.dataset.selected === 'true');
      this.updateVariant();
      if (this.dataset.updatePrices === 'true') {
        this.priceObserver = new MutationObserver(() => {
          window.queueMicrotask(() => this.syncVisiblePrice());
        });
        this.priceObserver.observe(this, { childList: true, subtree: true });
      }
    }

    syncVisiblePrice() {
      if (this.dataset.updatePrices !== 'true') return;
      const price = Number(this.dataset.price || 0);
      const comparePrice = Number(this.dataset.comparePrice || 0);
      const moneyFormat = this.dataset.moneyFormat;
      const formattedPrice = formatMoney(price, moneyFormat);
      const formattedCompare = comparePrice > price ? formatMoney(comparePrice, moneyFormat) : '';
      this.querySelectorAll('.bundle-deals__price-js, .regular-price').forEach((node) => {
        if (node.textContent !== formattedPrice) node.textContent = formattedPrice;
      });
      this.querySelectorAll('.bundle-deals__compare-price-js, .compare-price').forEach((node) => {
        if (node.textContent !== formattedCompare) node.textContent = formattedCompare;
        node.classList.toggle('hidden', comparePrice <= price);
      });
    }

    updateVariant() {
      if (!this.variants.length) return;
      const picker = this.querySelector('.upsell__variant-picker') || this;
      const variant = findVariant(this.variants, selectedValues(picker));
      if (!variant) return;
      this.dataset.id = String(variant.id);
      this.dataset.variantAvailable = String(Boolean(variant.available));
      if (this.variantInput) this.variantInput.value = String(variant.id);
      const checkbox = this.querySelector('.bundle-deals__checkbox-js');
      if (checkbox) {
        const percentage = Number(this.dataset.percentageLeft || 1);
        const fixed = Number(this.dataset.fixedDiscount || 0);
        const price = Math.max(0, Number(variant.price || 0) * percentage - fixed);
        const comparePrice = Math.max(Number(variant.compare_at_price || 0), Number(variant.price || 0));
        checkbox.dataset.id = String(variant.id);
        checkbox.dataset.price = String(price);
        checkbox.dataset.comparePrice = String(comparePrice);
        checkbox.disabled = !variant.available;
        if (!variant.available) checkbox.checked = false;
        if (this.dataset.updatePrices === 'true') {
          this.dataset.price = String(price);
          this.dataset.comparePrice = String(comparePrice);
          this.syncVisiblePrice();
        }
      }
      this.closest('main-bundle-offer')?.refresh?.();
    }

    setSelected(selected) {
      this.dataset.selected = String(selected);
      this.classList.toggle('upsell--selected', selected);
      this.toggleButton?.setAttribute('aria-pressed', String(selected));
    }

    disconnectedCallback() {
      this.priceObserver?.disconnect();
    }
  }
  define('product-info-upsell', ProductInfoUpsell);

  class MainBundleOffer extends HTMLElement {
    connectedCallback() {
      if (this.dataset.runtimeBound) return;
      this.dataset.runtimeBound = 'true';
      this.addEventListener('change', (event) => {
        if (event.target.matches('.bundle-deals__variant-selects-js select')) {
          this.updateItemVariant(event.target);
        }
        window.queueMicrotask(() => this.refresh());
      });
      this.refresh();
    }

    updateItemVariant(select) {
      const picker = select.closest('.bundle-deals__variant-selects-js');
      const item = picker?.closest('product-info-upsell');
      if (!picker || !item || item.dataset.updatePrices !== 'true') return;
      const variant = findVariant(parseJsonScript(picker), selectedValues(picker));
      if (!variant) return;
      const percentage = Number(item.dataset.percentageLeft || 1);
      const fixed = Number(item.dataset.fixedDiscount || 0);
      const price = Math.max(0, Number(variant.price || 0) * percentage - fixed);
      const comparePrice = Math.max(Number(variant.compare_at_price || 0), Number(variant.price || 0));
      const checkbox = item.querySelector('.bundle-deals__checkbox-js');

      item.dataset.id = String(variant.id);
      item.dataset.price = String(price);
      item.dataset.comparePrice = String(comparePrice);
      if (checkbox) {
        checkbox.dataset.id = String(variant.id);
        checkbox.dataset.price = String(price);
        checkbox.dataset.comparePrice = String(comparePrice);
        checkbox.disabled = !variant.available;
        if (!variant.available) checkbox.checked = false;
      }
      item.dataset.variantAvailable = String(Boolean(variant.available));

      // MainBundleOffer is the component that always receives the bubbling
      // picker change and recalculates the correct total. Update the visible
      // price in that same authoritative path instead of depending on the
      // child custom-element lifecycle used by a particular block renderer.
      const moneyFormat = item.dataset.moneyFormat || this.dataset.moneyFormat;
      item.querySelectorAll('.bundle-deals__price-js').forEach((node) => {
        node.textContent = formatMoney(price, moneyFormat);
      });
      item.querySelectorAll('.bundle-deals__compare-price-js').forEach((node) => {
        node.textContent = comparePrice > price ? formatMoney(comparePrice, moneyFormat) : '';
        node.classList.toggle('hidden', comparePrice <= price);
        node.hidden = comparePrice <= price;
      });
    }

    refresh() {
      const selected = Array.from(this.querySelectorAll('product-info-upsell[data-selected="true"]'))
        .filter((item) => !item.querySelector('.bundle-deals__checkbox-js')?.disabled);
      // The legacy bundle listener can refresh totals after ProductInfoUpsell
      // has already resolved the new variant while leaving its rendered line
      // price stale. Re-apply the authoritative item datasets at the final
      // Main-offer refresh boundary so execution order cannot split the two.
      selected.forEach((item) => {
        if (item.dataset.updatePrices !== 'true') return;
        if (typeof item.syncVisiblePrice === 'function') {
          item.syncVisiblePrice();
          return;
        }
        const itemPrice = Number(item.dataset.price || 0);
        const itemCompare = Number(item.dataset.comparePrice || 0);
        item.querySelectorAll('.bundle-deals__price-js, .regular-price').forEach((node) => {
          node.textContent = formatMoney(itemPrice, item.dataset.moneyFormat || this.dataset.moneyFormat);
        });
        item.querySelectorAll('.bundle-deals__compare-price-js, .compare-price').forEach((node) => {
          node.textContent = itemCompare > itemPrice
            ? formatMoney(itemCompare, item.dataset.moneyFormat || this.dataset.moneyFormat)
            : '';
          node.classList.toggle('hidden', itemCompare <= itemPrice);
          node.hidden = itemCompare <= itemPrice;
        });
      });
      const percentage = Number(this.dataset.percentageLeft || 1);
      const fixed = Number(this.dataset.fixedDiscount || 0);
      const total = Math.max(0, selected.reduce((sum, item) => sum + Number(item.dataset.price || 0), 0) * percentage - fixed);
      const compare = selected.reduce((sum, item) => sum + Number(item.dataset.comparePrice || 0), 0);
      const priceNode = this.querySelector('.bundle-deals__total-price-js');
      const compareNode = this.querySelector('.bundle-deals__total-compare-price-js');
      if (priceNode) priceNode.textContent = formatMoney(total, this.dataset.moneyFormat);
      if (compareNode) {
        compareNode.textContent = compare > total ? formatMoney(compare, this.dataset.moneyFormat) : '';
        compareNode.hidden = compare <= total;
      }
      dispatch(this, 'bundle:change', { items: selected.map((item) => Number(item.dataset.id)), total });
    }
  }
  define('main-bundle-offer', MainBundleOffer);

  class ComparisonSlider extends HTMLElement {
    connectedCallback() {
      const range = this.querySelector('input[type="range"]');
      if (!range || range.dataset.runtimeBound) return;
      range.dataset.runtimeBound = 'true';
      const update = () => this.style.setProperty('--position', `${range.value}%`);
      range.addEventListener('input', update);
      update();
    }
  }
  define('comparison-slider', ComparisonSlider);

  class InternalVideo extends HTMLElement {
    connectedCallback() {
      if (this.dataset.runtimeBound) return;
      this.dataset.runtimeBound = 'true';
      this.video = this.querySelector('video');
      if (!this.video) return;
      this.querySelectorAll('[data-play], .internal-video__play, .play-btn').forEach((button) => button.addEventListener('click', () => this.toggle()));
      this.querySelectorAll('[data-sound], .internal-video__sound').forEach((button) => button.addEventListener('click', () => {
        this.video.muted = !this.video.muted;
        this.dataset.muted = String(this.video.muted);
      }));
      if (this.dataset.autoplay === 'true') this.observe();
    }

    toggle() {
      if (this.video.paused) this.video.play().catch(() => {});
      else this.video.pause();
      this.dataset.playing = String(!this.video.paused);
    }

    observe() {
      this.observer = new IntersectionObserver(([entry]) => {
        if (entry.isIntersecting) this.video.play().catch(() => {});
        else this.video.pause();
      }, { threshold: 0.35 });
      this.observer.observe(this);
    }

    disconnectedCallback() {
      this.observer?.disconnect();
    }
  }
  define('internal-video', InternalVideo);

  class DynamicDates extends HTMLElement {
    connectedCallback() {
      const days = (this.dataset.dayLabels || '').split(',').map((value) => value.trim());
      const months = (this.dataset.monthLabels || '').split(',').map((value) => value.trim());
      const today = new Date();
      this.querySelectorAll('[data-dynamic-date="true"]').forEach((element) => {
        const normalize = (value) => Number.isFinite(Number(value)) ? Math.min(3650, Math.max(0, Math.floor(Number(value)))) : 0;
        const min = normalize(element.dataset.minDays);
        const max = Math.max(min, normalize(element.dataset.maxDays ?? min));
        const start = this.addDays(min, today);
        const end = this.addDays(max, today);
        element.hidden = !start || !end;
        if (element.hidden) return;
        const template = element.dataset.text || element.textContent;
        element.textContent = template
          .replace(/\[start_date\]/g, this.format(start, days, months))
          .replace(/\[end_date\]/g, this.format(end, days, months));
      });
    }

    addDays(amount, today = new Date()) {
      const value = new Date(today);
      value.setHours(12, 0, 0, 0);
      const weekdays = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
      const parse = (input) => new Set((input || '').toLowerCase().split(',').map((item) => item.trim()).filter(Boolean));
      const skipDays = parse(this.dataset.daysToSkip);
      const skipDates = parse(this.dataset.datesToSkip);
      const excluded = () => skipDays.has(weekdays[value.getDay()]) || skipDates.has(`${String(value.getMonth() + 1).padStart(2, '0')}-${String(value.getDate()).padStart(2, '0')}`);
      let remaining = amount;
      for (let guard = 0; guard < 36500; guard += 1) {
        if (remaining === 0 && !excluded()) return value;
        value.setDate(value.getDate() + 1);
        if (!excluded() && remaining > 0) remaining -= 1;
      }
      return null;
    }

    format(date, days, months) {
      const format = this.dataset.dateFormat || 'mm_dd';
      const month = months[date.getMonth()] || date.toLocaleString(document.documentElement.lang || 'en', { month: 'short' });
      const day = days[(date.getDay() + 6) % 7] || date.toLocaleString(document.documentElement.lang || 'en', { weekday: 'short' });
      const number = date.getDate();
      const suffix = number % 100 >= 11 && number % 100 <= 13 ? 'th' : ({ 1: 'st', 2: 'nd', 3: 'rd' }[number % 10] || 'th');
      const numeric = `${String(number).padStart(2, '0')}. ${String(date.getMonth() + 1).padStart(2, '0')}.`;
      const formats = {
        day_mm_dd: `${day}, ${month} ${number}${suffix}`,
        day_dd_mm: `${day}, ${number}. ${month}`,
        mm_dd: `${month} ${number}${suffix}`,
        dd_mm: `${number}. ${month}`,
        dd_mm_no_dot: `${number} ${month}`,
        day_dd_mm_numeric: `${day}, ${numeric}`,
        dd_mm_numeric: numeric,
      };
      return formats[format] || formats.mm_dd;
    }
  }
  define('dynamic-dates', DynamicDates);

  class ClickableDiscount extends HTMLElement {
    connectedCallback() {
      const button = this.querySelector('.clickable-discount__btn');
      if (!button || button.dataset.runtimeBound) return;
      button.dataset.runtimeBound = 'true';
      button.addEventListener('click', async () => {
        this.dataset.loading = 'true';
        try {
          const response = await fetch(`/discount/${encodeURIComponent(this.dataset.code || '')}`, {
            credentials: 'same-origin',
            redirect: 'follow',
          });
          if (!response.ok) throw new Error('Discount could not be applied');
          this.dataset.applied = 'true';
          this.dataset.error = 'false';
          button.setAttribute('aria-pressed', 'true');
        } catch (_error) {
          this.dataset.error = 'true';
          this.dataset.applied = 'false';
        } finally {
          this.dataset.loading = 'false';
        }
      });
    }
  }
  define('clickable-discount', ClickableDiscount);

  class CustomProductField extends HTMLElement {
    connectedCallback() {
      if (this.dataset.runtimeBound) return;
      this.dataset.runtimeBound = 'true';
      const form = this.closest('product-form, form')?.querySelector('form') || this.closest('form');
      const control = this.querySelector('input, textarea, select');
      if (!form || !control || !this.dataset.name) return;
      const hidden = document.createElement('input');
      hidden.type = 'hidden';
      hidden.name = `properties[${this.dataset.name}]`;
      hidden.dataset.customProductFieldMirror = this.dataset.name;
      form.append(hidden);
      const update = () => {
        const checked = this.querySelector('input[type="radio"]:checked');
        hidden.value = checked?.value ?? control.value ?? '';
        const required = this.hasAttribute('data-required') || control.required;
        this.classList.toggle('field-error', required && !hidden.value.trim());
      };
      this.addEventListener('change', update);
      this.addEventListener('input', update);
      update();
    }
  }
  define('custom-product-field', CustomProductField);

  class CardVariantSelects extends HTMLElement {
    connectedCallback() {
      if (this.dataset.runtimeBound) return;
      this.dataset.runtimeBound = 'true';
      this.variants = parseJsonScript(this);
      this.addEventListener('change', () => this.update());
      this.update();
    }

    update() {
      const values = Array.from(this.querySelectorAll('select, fieldset')).map((group) =>
        group.matches('select') ? group.value : group.querySelector('input:checked')?.value
      ).filter((value) => value !== undefined);
      const variant = findVariant(this.variants, values);
      const formId = this.getAttribute('if') || this.dataset.section;
      const form = document.getElementById(formId) || document.getElementById(`${formId}-submit`)?.form;
      const idInput = form?.querySelector('input[name="id"]');
      if (idInput && variant) idInput.value = variant.id;
      const card = this.closest('.card-wrapper, .card');
      const button = card?.querySelector('[type="submit"]');
      if (button) button.disabled = !variant?.available;
      const image = card?.querySelector('.card__media img, .media img');
      const source = variant?.featured_media?.preview_image?.src || variant?.featured_image?.src;
      if (image && source) image.src = source;
      dispatch(this, 'variant:change', { variant });
    }
  }
  define('card-variant-selects', CardVariantSelects);

  class QuantityBreakUpsells extends HTMLElement {
    connectedCallback() {
      if (this.dataset.runtimeBound) return;
      this.dataset.runtimeBound = 'true';
      this.querySelectorAll('[data-toggle="true"]').forEach((item) => item.addEventListener('click', (event) => {
        if (event.target.closest('select, a, button')) return;
        item.dataset.selected = String(item.dataset.selected !== 'true');
        dispatch(this, 'upsell:change', { item, selected: item.dataset.selected === 'true' });
      }));
    }
  }
  define('quantity-break-upsells', QuantityBreakUpsells);

  class QuantityBreaks extends HTMLElement {
    connectedCallback() {
      if (this.dataset.runtimeBound) return;
      this.dataset.runtimeBound = 'true';
      this.addEventListener('change', (event) => {
        const quantity = event.target.closest('input[name="quantity"]');
        if (quantity) this.applyQuantity(quantity);
        const select = event.target.closest('select');
        if (select) this.updateOfferVariant(select.closest('[data-index], .quantity-break'));
      });
      const selected = this.querySelector('input[name="quantity"]:checked');
      if (selected) this.applyQuantity(selected);
    }

    applyQuantity(input) {
      const amount = Number(input.dataset.quantity || input.value || 1);
      document.querySelectorAll(`#Quantity-${this.dataset.section}, #quantity-${this.dataset.section}, input[name="quantity"]`).forEach((field) => {
        if (!this.contains(field)) {
          field.value = amount;
          field.dispatchEvent(new Event('change', { bubbles: true }));
        }
      });
      this.querySelectorAll('quantity-break-upsells').forEach((upsell) => {
        upsell.hidden = Number(upsell.dataset.offer) !== Number(input.dataset.index || input.value || 1);
      });
      dispatch(this, 'quantity-break:change', { quantity: amount, input });
    }

    updateOfferVariant(container) {
      if (!container) return;
      const variants = parseJsonScript(container);
      const variant = findVariant(variants, selectedValues(container));
      if (!variant) return;
      container.dataset.selectedId = variant.id;
      container.querySelectorAll('[data-product], input[name="id"]').forEach((element) => {
        if (element.matches('input')) element.value = variant.id;
        else element.dataset.product = variant.id;
      });
    }
  }
  define('quantity-breaks', QuantityBreaks);

  class QuantityGifts extends HTMLElement {
    connectedCallback() {
      if (this.dataset.runtimeBound) return;
      this.dataset.runtimeBound = 'true';
      this.quantityInput = document.querySelector(`#Quantity-${this.dataset.section}, #quantity-${this.dataset.section}, input[name="quantity"]`);
      this.quantityInput?.addEventListener('change', () => this.update());
      document.addEventListener('quantity-break:change', (event) => this.update(event.detail.quantity));
      this.update();
    }

    update(quantity = Number(this.quantityInput?.value || 1)) {
      this.querySelectorAll('.quantity-gift').forEach((gift) => {
        const unlocked = quantity >= Number(gift.dataset.quantity || 1);
        gift.dataset.unlocked = String(unlocked);
        gift.classList.toggle('quantity-gift--unlocked', unlocked);
        const form = gift.querySelector('product-form, form');
        if (form) form.dataset.selected = String(unlocked);
      });
      dispatch(this, 'quantity-gifts:change', { quantity });
    }
  }
  define('quantity-gifts', QuantityGifts);

  class SubscriptionWidget extends HTMLElement {
    selectedPlanControl() {
      return this.querySelector('select option:checked, input[data-selling-plan-id]:checked') || this.querySelector('[data-selling-plan-id]') || {
        value: this.dataset.firstSellingPlanId || '',
        dataset: {
          sellingPlanId: this.dataset.firstSellingPlanId || '',
          price: this.dataset.firstPlanPrice,
          comparePrice: this.dataset.firstPlanComparePrice,
          discountType: this.dataset.discountType,
          discountValue: this.dataset.discountValue,
        },
      };
    }

    beginVariantUpdate() {
      this.dataset.variantPending = 'true';
      this.setAttribute('aria-busy', 'true');
      if (this.hiddenInput) this.hiddenInput.disabled = true;
    }

    refreshFromProduct(html, variant, sourceSection) {
      const source = html.getElementById(`SubscriptionWidget-${sourceSection}`);
      if (!source || String(source.dataset.variantId) !== String(variant.id)) {
        this.beginVariantUpdate();
        throw new Error('Subscription data does not match the selected variant');
      }
      const section = this.dataset.section;
      const previousMode = this.querySelector('.subscription__box[data-selected="true"]')?.dataset.type;
      const previousPlan = String(this.selectedPlanControl()?.value || '');
      // Refresh the server-calculated allocations and all editable price text,
      // retaining this instance's IDs and the shopper's still-valid choice.
      Object.assign(this.dataset, source.dataset, { section, variantPending: 'false' });
      this.innerHTML = source.innerHTML.split(sourceSection).join(section);
      this.planNotice = null;
      this.removeAttribute('aria-busy');
      this.hidden = !this.dataset.firstSellingPlanId;
      this.classList.toggle('hidden', this.hidden);
      if (this.hidden) {
        this.setPlanInput('', false);
        return this.dataset.requiresPlan !== 'true';
      }
      const plans = Array.from(this.querySelectorAll('[data-selling-plan-id]'));
      const retained = plans.find((plan) => String(plan.value) === previousPlan) || plans[0];
      plans.forEach((plan) => {
        if (plan.tagName === 'OPTION') plan.selected = plan === retained;
        else if (plan.type === 'radio') plan.checked = plan === retained;
      });
      this.updateSubscriptionPrices(this.selectedPlanControl());
      const mode = this.dataset.requiresPlan === 'true' ? 'subscription' : previousMode;
      const box = Array.from(this.querySelectorAll('.subscription__box')).find((item) => item.dataset.type === mode)
        || this.querySelector('.subscription__box[data-selected="true"]') || this.querySelector('.subscription__box');
      if (box) this.selectMode(box);
      return true;
    }

    connectedCallback() {
      if (this.onModeClick) return;
      this.onModeClick = (event) => {
        const box = event.target.closest('.subscription__box');
        if (box) this.selectMode(box);
      };
      this.onPlanChange = (event) => {
        if (event.target.matches('[data-selling-plan-id], #SubscriptionFrequency-' + this.dataset.section)) this.updatePlan(event.target);
      };
      this.addEventListener('click', this.onModeClick);
      this.addEventListener('change', this.onPlanChange);
      const selected = this.querySelector('.subscription__box[data-selected="true"]') || this.querySelector('.subscription__box');
      if (selected) this.selectMode(selected);
      const productRoot = this.closest('product-info') || this.parentElement;
      if (productRoot) {
        this.formObserver = new MutationObserver(() => this.syncPlanInput());
        this.formObserver.observe(productRoot, { childList: true, subtree: true });
      }
    }

    disconnectedCallback() {
      this.formObserver?.disconnect();
      this.form?.removeEventListener('submit', this.onFormSubmit, true);
      this.removeEventListener('click', this.onModeClick);
      this.removeEventListener('change', this.onPlanChange);
      this.hiddenInput?.remove();
      this.hiddenInput = null;
      this.form = null;
      this.onModeClick = null;
      this.onPlanChange = null;
    }

    ensurePlanInput() {
      // Never adopt a same-name field: an app may own it, even while disabled.
      const form = document.getElementById(`product-form-${this.dataset.section}`)
        || this.closest('product-info')?.querySelector('product-form form[action*="/cart/add"]');
      if (form !== this.form) {
        this.form?.removeEventListener('submit', this.onFormSubmit, true);
        this.hiddenInput?.remove();
        this.hiddenInput = null;
        this.form = form;
        this.onFormSubmit ||= () => this.syncPlanInput();
        this.form?.addEventListener('submit', this.onFormSubmit, true);
      }
      if (!this.form) return false;
      const fields = Array.from(this.form.querySelectorAll('[name="selling_plan"]'));
      Array.from(this.form.elements || []).forEach((field) => {
        if (field.name === 'selling_plan' && !fields.includes(field)) fields.push(field);
      });
      if (fields.some((field) => field !== this.hiddenInput)) {
        if (this.hiddenInput) this.hiddenInput.disabled = true;
        this.dataset.planManaged = 'external';
        this.setPlanNotice(true);
        return false;
      }
      this.dataset.planManaged = 'theme';
      this.setPlanNotice(false);
      if (!this.hiddenInput || this.hiddenInput.parentNode !== this.form) {
        this.hiddenInput = document.createElement('input');
        this.hiddenInput.type = 'hidden';
        this.hiddenInput.name = 'selling_plan';
        this.hiddenInput.dataset.generalStorePlan = 'true';
        this.hiddenInput.disabled = true;
        this.form.append(this.hiddenInput);
      }
      return true;
    }

    setPlanNotice(visible) {
      if (!visible) {
        this.planNotice?.remove();
        this.planNotice = null;
        return;
      }
      if (this.planNotice) return;
      this.planNotice = document.createElement('p');
      this.planNotice.setAttribute('role', 'status');
      this.planNotice.textContent = 'Purchase options are managed by the subscription app. Please use its selector.';
      this.append(this.planNotice);
    }

    selectMode(box) {
      if (this.dataset.variantPending === 'true') return;
      if (this.dataset.requiresPlan === 'true' && box.dataset.type !== 'subscription') {
        box = this.querySelector('.subscription__box--subscription');
      }
      if (!box) return;
      if (!this.ensurePlanInput()) return;
      this.querySelectorAll('.subscription__box').forEach((item) => {
        const active = item === box;
        item.dataset.selected = String(active);
        item.classList.toggle('subscription__box--selected', active);
      });
      const subscription = box.dataset.type === 'subscription';
      const selector = this.selectedPlanControl();
      const sellingPlanId = subscription ? (selector?.dataset.sellingPlanId || selector?.value || this.dataset.firstSellingPlanId || '') : '';
      this.setPlanInput(sellingPlanId, subscription);
      if (subscription && selector) {
        this.updateSubscriptionPrices(selector);
        this.updateMainProductPrice(Number(selector.dataset.price || 0));
      } else {
        this.updateMainProductPrice(Number(this.dataset.basePrice || 0));
      }
    }

    updatePlan(control) {
      if (this.dataset.variantPending === 'true') return;
      if (!this.ensurePlanInput()) return;
      const selected = control.matches('select') ? control.selectedOptions[0] : control;
      const sellingPlanId = selected?.dataset.sellingPlanId || selected?.value || '';
      this.setPlanInput(sellingPlanId, true);
      this.updateSubscriptionPrices(selected);
      this.updateMainProductPrice(Number(selected?.dataset.price || 0));
    }

    setPlanInput(sellingPlanId, subscription) {
      if (!this.ensurePlanInput()) return false;
      this.hiddenInput.disabled = !subscription || !sellingPlanId;
      this.hiddenInput.value = subscription ? sellingPlanId : '';
      this.hiddenInput.setAttribute('value', this.hiddenInput.value);
      return true;
    }

    updateSubscriptionPrices(selected) {
      if (!selected) return;
      const price = Number(selected.dataset.price || 0);
      let comparePrice = Number(selected.dataset.comparePrice ?? price);
      if (this.dataset.subCompareBasedOn === 'compare') comparePrice = Math.max(comparePrice, Number(this.dataset.baseComparePrice || 0));
      const amountSaved = Math.max(comparePrice - price, 0);
      const percentageSaved = comparePrice > 0 ? Math.floor(amountSaved * 100 / comparePrice) : 0;
      const values = {
        '[price]': formatMoney(price, this.dataset.moneyFormat),
        '[compare_price]': formatMoney(comparePrice, this.dataset.moneyFormat),
        '[amount_saved]': formatMoney(amountSaved, this.dataset.moneyFormat),
        '[amount_saved_rounded]': formatMoney(Math.round(amountSaved / 100) * 100, this.dataset.moneyFormat),
        '[percentage_saved]': `${percentageSaved}%`,
        '[subscription_name]': this.dataset.subscriptionName || '',
      };
      this.querySelectorAll('.sub-price-update[data-text]').forEach((element) => {
        let html = element.dataset.text || '';
        Object.entries(values).forEach(([token, value]) => { html = html.split(token).join(value); });
        element.innerHTML = html;
      });
    }

    updateMainProductPrice(price) {
      if (this.dataset.updatePrices === 'false') return;
      const priceRoot = document.getElementById(`price-${this.dataset.section}`);
      if (!priceRoot || !Number.isFinite(price) || price < 0) return;
      const comparePrice = Number(this.dataset.baseComparePrice || price);
      const productInfo = this.closest('product-info');
      if (typeof productInfo?.updateVisiblePrice === 'function') {
        const available = !priceRoot.querySelector('.price')?.classList.contains('price--sold-out');
        productInfo.updateVisiblePrice({ price, compare_at_price: comparePrice, available });
        return;
      }
      const formattedPrice = formatMoney(price, this.dataset.moneyFormat);
      priceRoot.querySelectorAll('.main-price').forEach((element) => { element.textContent = formattedPrice; });
      const amountSaved = Math.max(comparePrice - price, 0);
      const percentageSaved = comparePrice > 0 ? Math.floor(amountSaved * 100 / comparePrice) : 0;
      priceRoot.querySelectorAll('.badge__percentage-saved').forEach((element) => { element.textContent = String(percentageSaved); });
      priceRoot.querySelectorAll('.badge__money-saved').forEach((element) => {
        element.textContent = formatMoney(amountSaved, this.dataset.moneyFormat).replace(/\.00(?=\D*$)/, '');
      });
    }

    syncPlanInput() {
      if (this.dataset.variantPending === 'true') return;
      const subscription = this.querySelector('.subscription__box--subscription')?.dataset.selected === 'true';
      const selected = this.selectedPlanControl();
      this.setPlanInput(subscription ? (selected?.dataset.sellingPlanId || selected?.value || this.dataset.firstSellingPlanId || '') : '', subscription);
    }
  }
  define('subscription-widget', SubscriptionWidget);

  class StickyAtc extends HTMLElement {
    connectedCallback() {
      if (this.dataset.runtimeBound) return;
      this.dataset.runtimeBound = 'true';
      this.mainButton = document.querySelector(`#ProductSubmitButton-${this.dataset.section}, [data-product-section="${this.dataset.section}"] [name="add"], product-form [name="add"]`);
      this.button = this.querySelector('.sticky-atc__add_button');
      this.button?.addEventListener('click', (event) => {
        if (this.dataset.scrollBtn === 'true') return this.scrollToDestination(event);
        if (this.mainButton && this.button.type === 'button') {
          event.preventDefault();
          this.mainButton.click();
        }
      });
      this.addEventListener('change', (event) => this.syncVariant(event.target));
      if (this.dataset.afterScroll === 'true' && this.mainButton) this.observeMainButton();
    }

    observeMainButton() {
      this.onScroll = () => {
        const passedMainButton = this.mainButton.getBoundingClientRect().bottom < 0;
        this.classList.toggle('active', passedMainButton);
        this.toggleAttribute('hidden', !passedMainButton);
      };
      window.addEventListener('scroll', this.onScroll, { passive: true });
      this.onScroll();
    }

    syncVariant(control) {
      if (!control.matches('select')) return;
      const picker = control.closest('[data-section]');
      const variants = parseJsonScript(picker || this);
      const combinedId = control.selectedOptions[0]?.dataset.variantId;
      const variant = combinedId
        ? variants.find((item) => String(item.id) === String(combinedId))
        : findVariant(variants, selectedValues(picker || this));
      if (!variant) return;
      const mainId = document.querySelector(`form[id*="${this.dataset.section}"] input[name="id"]`);
      if (mainId) {
        mainId.value = variant.id;
        mainId.dispatchEvent(new Event('change', { bubbles: true }));
      }
      if (this.button) this.button.disabled = !variant.available;
      dispatch(this, 'variant:change', { variant });
    }

    scrollToDestination(event) {
      event.preventDefault();
      const selector = this.dataset.scrollDestination;
      const target = selector && (document.getElementById(selector) || document.querySelector(`[data-${selector}]`) || document.querySelector(selector));
      target?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }

    disconnectedCallback() {
      if (this.onScroll) window.removeEventListener('scroll', this.onScroll);
    }
  }
  define('sticky-atc', StickyAtc);

  class BundleDeals extends HTMLElement {
    connectedCallback() {
      if (this.dataset.runtimeBound) return;
      this.dataset.runtimeBound = 'true';
      this.addEventListener('change', (event) => {
        if (event.target.matches('.bundle-deals__checkbox-js')) event.target.dataset.checked = String(event.target.checked);
        if (event.target.matches('.bundle-deals__variant-selects-js select')) this.updateVariant(event.target.closest('.bundle-deals__variant-selects-js'));
        this.refresh();
      });
      this.refresh();
    }

    updateVariant(container) {
      const variants = parseJsonScript(container);
      const variant = findVariant(variants, selectedValues(container));
      const index = container.dataset.index;
      const checkbox = this.querySelector(`.bundle-deals__checkbox-js[data-index="${index}"]`);
      if (!checkbox) return;
      const item = container.closest('product-info-upsell, .upsell');
      if (!variant) {
        checkbox.dataset.id = '';
        checkbox.disabled = true;
        checkbox.checked = false;
        if (item) {
          item.dataset.id = '';
          item.dataset.variantAvailable = 'false';
        }
        return;
      }
      const percentage = Number(item?.dataset.percentageLeft || container.dataset.percentageLeft || 1);
      const fixed = Number(item?.dataset.fixedDiscount || container.dataset.fixedDiscount || 0);
      checkbox.dataset.id = variant.id;
      checkbox.dataset.price = Math.max(0, Number(variant.price) * percentage - fixed);
      checkbox.dataset.comparePrice = Math.max(Number(variant.compare_at_price || 0), Number(variant.price || 0));
      checkbox.disabled = !variant.available;
      if (!variant.available) checkbox.checked = false;
      else if (checkbox.dataset.checked === 'true') checkbox.checked = true;
      if (item) item.dataset.variantAvailable = String(Boolean(variant.available));

      // Main offers use the shared bundle-deals change path as well. Keep the
      // visible line-item price in sync here, including legacy markup where
      // the item wrapper is not upgraded to product-info-upsell.
      if (item?.dataset.updatePrices === 'true') {
        item.dataset.id = String(variant.id);
        item.dataset.price = checkbox.dataset.price;
        item.dataset.comparePrice = checkbox.dataset.comparePrice;
        item.querySelectorAll('.bundle-deals__price-js, .regular-price').forEach((node) => {
          node.textContent = formatMoney(Number(checkbox.dataset.price || 0), item.dataset.moneyFormat || this.dataset.moneyFormat);
        });
        item.querySelectorAll('.bundle-deals__compare-price-js, .compare-price').forEach((node) => {
          const compare = Number(checkbox.dataset.comparePrice || 0);
          const price = Number(checkbox.dataset.price || 0);
          node.textContent = compare > price ? formatMoney(compare, item.dataset.moneyFormat || this.dataset.moneyFormat) : '';
          node.classList.toggle('hidden', compare <= price);
          node.hidden = compare <= price;
        });
      }
    }

    resolveVariantsHost() {
      const localHost = this.querySelector('.product-form__variants');
      if (localHost) return localHost;

      const siblingForm = this.nextElementSibling;
      if (
        siblingForm?.matches('product-form') &&
        (!this.dataset.section || siblingForm.dataset.section === this.dataset.section)
      ) {
        return siblingForm.querySelector('.product-form__variants');
      }

      const bundle = this.closest('.bundle-offer');
      const matchingForm = Array.from(bundle?.querySelectorAll('product-form') || []).find(
        (form) => !this.dataset.section || form.dataset.section === this.dataset.section,
      );
      return matchingForm?.querySelector('.product-form__variants') || null;
    }

    refresh() {
      const selected = Array.from(this.querySelectorAll('.bundle-deals__checkbox-js:checked:not(:disabled)'));
      const percentage = Number(this.dataset.percentageLeft || 1);
      const fixed = Number(this.dataset.fixedDiscount || 0);
      const total = Math.max(0, selected.reduce((sum, item) => sum + Number(item.dataset.price || 0), 0) * percentage - fixed);
      const compare = selected.reduce((sum, item) => sum + Number(item.dataset.comparePrice || 0), 0);
      const priceNode = this.querySelector('.bundle-deals__total-price-js');
      const compareNode = this.querySelector('.bundle-deals__total-compare-price-js');
      if (priceNode) priceNode.textContent = formatMoney(total);
      if (compareNode) {
        compareNode.textContent = compare > total ? formatMoney(compare) : '';
        compareNode.hidden = compare <= total;
      }
      const variantsHost = this.resolveVariantsHost();
      if (variantsHost) {
        variantsHost.innerHTML = '';
        selected.forEach((item) => {
          const input = document.createElement('input');
          input.type = 'hidden';
          input.name = 'items[][id]';
          input.value = item.dataset.id;
          variantsHost.append(input);
          const quantity = document.createElement('input');
          quantity.type = 'hidden';
          quantity.name = 'items[][quantity]';
          quantity.value = '1';
          variantsHost.append(quantity);
        });
      }
      const submit = this.querySelector('[type="submit"]');
      if (submit) submit.disabled = selected.length === 0;
      dispatch(this, 'bundle:change', { items: selected.map((item) => Number(item.dataset.id)), total });
    }
  }
  define('bundle-deals', BundleDeals);

  class CartDrawerGift extends HTMLElement {
    connectedCallback() {
      if (this.dataset.runtimeBound) return;
      this.dataset.runtimeBound = 'true';
      document.addEventListener('cart:updated', (event) => this.update(event.detail.cart));
      this.update();
    }

    update(cart = null) {
      const basis = this.dataset.type === 'product_quantity'
        ? Number(cart?.item_count ?? document.querySelector('[data-cart-item-count]')?.dataset.cartItemCount ?? 0)
        : Number(cart?.total_price ?? document.querySelector('[data-cart-total]')?.dataset.cartTotal ?? 0);
      const unlocked = basis >= Number(this.dataset.requirement || 0);
      this.dataset.selected = String(unlocked);
      this.classList.toggle('is-unlocked', unlocked);
    }
  }
  define('cart-drawer-gift', CartDrawerGift);

  class PromoPopup extends HTMLElement {
    connectedCallback() {
      if (this.dataset.runtimeBound) return;
      this.dataset.runtimeBound = 'true';
      this.abortController = new AbortController();
      this.overlay = this.querySelector('.sign-up-popup-overlay');
      this.modal = this.querySelector('.sign-up-popup-modal');
      this.sectionRoot = this.closest('.shopify-section') || this.parentElement;
      this.successOverlay = this.sectionRoot?.querySelector('.success-popup-overlay');
      this.successModal = this.sectionRoot?.querySelector('.success-popup-modal');
      this.storageKey = `promo-popup-dismissed-${window.location.host}`;
      this.mode = this.dataset.mode || (this.dataset.testMode === 'true' ? 'testing' : 'enabled');
      this.isTestMode = this.mode === 'testing';

      const signal = this.abortController.signal;
      this.querySelectorAll('.promp-popup__close-btn').forEach((button) => {
        button.addEventListener('click', () => this.close({ remember: !this.isTestMode }), { signal });
      });
      this.successOverlay?.addEventListener('click', () => this.closeSuccess(), { signal });
      this.successModal?.querySelectorAll('.popup-modal__close, .popup-modal__dismiss-btn').forEach((button) => {
        button.addEventListener('click', () => this.closeSuccess(), { signal });
      });
      document.addEventListener('keydown', (event) => {
        if (event.key !== 'Escape') return;
        if (this.successModal?.classList.contains('popup-modal--active')) this.closeSuccess();
        else if (this.isOpen()) this.close({ remember: !this.isTestMode });
      }, { signal });

      if (this.mode === 'success_testing') {
        document.body.classList.add('overflow-hidden');
      } else if (this.isTestMode) {
        this.open();
      } else if (!this.wasRecentlyDismissed()) {
        this.openTimer = window.setTimeout(
          () => this.open(),
          Math.max(0, Number(this.dataset.delaySeconds) || 0) * 1000
        );
      }
    }

    disconnectedCallback() {
      window.clearTimeout(this.openTimer);
      window.clearInterval(this.countdownTimer);
      this.abortController?.abort();
      document.body.classList.remove('overflow-hidden');
      delete this.dataset.runtimeBound;
    }

    wasRecentlyDismissed() {
      try {
        const value = JSON.parse(window.localStorage.getItem(this.storageKey) || 'null');
        const dismissedAt = Number(value?.dismissedAt || 0);
        const delayDays = Math.max(0, Number(this.dataset.delayDays) || 0);
        return dismissedAt > 0 && Date.now() - dismissedAt < delayDays * 86400000;
      } catch (_error) {
        return false;
      }
    }

    rememberDismissal() {
      try {
        window.localStorage.setItem(this.storageKey, JSON.stringify({ dismissedAt: Date.now() }));
      } catch (_error) {
        // Storage can be unavailable in privacy-restricted browsers; the popup remains usable.
      }
    }

    isOpen() {
      return this.modal?.classList.contains('popup-modal--active');
    }

    open() {
      if (!this.modal || !this.overlay || this.isOpen()) return;
      this.previouslyFocused = document.activeElement;
      this.modal.classList.add('popup-modal--active');
      this.overlay.classList.add('popup-overlay--active');
      document.body.classList.add('overflow-hidden');
      this.startCountdown();
      window.requestAnimationFrame(() => {
        this.modal.querySelector('input, button, a[href], [tabindex]:not([tabindex="-1"])')?.focus();
      });
      dispatch(this, 'promo-popup:open');
    }

    close({ remember = true } = {}) {
      window.clearTimeout(this.openTimer);
      window.clearInterval(this.countdownTimer);
      this.modal?.classList.remove('popup-modal--active');
      this.overlay?.classList.remove('popup-overlay--active');
      document.body.classList.remove('overflow-hidden');
      if (remember) this.rememberDismissal();
      if (this.previouslyFocused instanceof HTMLElement) this.previouslyFocused.focus();
      dispatch(this, 'promo-popup:close');
    }

    closeSuccess() {
      this.successModal?.classList.remove('popup-modal--active');
      this.successOverlay?.classList.remove('popup-overlay--active');
      document.body.classList.remove('overflow-hidden');
      dispatch(this, 'promo-popup:success-close');
    }

    startCountdown() {
      if (this.dataset.displayTimer !== 'true') return;
      const minutesNode = this.querySelector('.popup-modal__timer__minutes');
      const secondsNode = this.querySelector('.popup-modal__timer__seconds');
      if (!minutesNode || !secondsNode) return;

      window.clearInterval(this.countdownTimer);
      let remaining = Math.max(0, Math.round((Number(this.dataset.timerDuration) || 0) * 60));
      const render = () => {
        minutesNode.textContent = String(Math.floor(remaining / 60)).padStart(2, '0');
        secondsNode.textContent = String(remaining % 60).padStart(2, '0');
      };
      render();
      this.countdownTimer = window.setInterval(() => {
        remaining = Math.max(0, remaining - 1);
        render();
        if (remaining === 0) window.clearInterval(this.countdownTimer);
      }, 1000);
    }
  }
  define('promo-popup', PromoPopup);

  class SectionGroup extends HTMLElement {
    connectedCallback() {
      if (this.dataset.runtimeBound) return;
      this.dataset.runtimeBound = 'true';
      this.abortController = new AbortController();
      this.moves = [];
      this.refresh = this.refresh.bind(this);

      const signal = this.abortController.signal;
      document.addEventListener('shopify:section:load', () => this.queueRefresh(), { signal });
      document.addEventListener('shopify:section:reorder', () => this.queueRefresh(), { signal });

      if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', this.refresh, { once: true, signal });
      } else {
        this.queueRefresh();
      }
    }

    disconnectedCallback() {
      window.cancelAnimationFrame(this.refreshFrame);
      this.abortController?.abort();
      this.restoreSections();
      delete this.dataset.runtimeBound;
    }

    queueRefresh() {
      window.cancelAnimationFrame(this.refreshFrame);
      this.refreshFrame = window.requestAnimationFrame(this.refresh);
    }

    resolveSection(selector) {
      const value = String(selector || '').trim();
      if (!value) return null;
      try {
        const normalized = value.startsWith('#') || value.startsWith('.') ? value : `#${CSS.escape(value)}`;
        return document.querySelector(normalized);
      } catch (_error) {
        return null;
      }
    }

    refresh() {
      this.restoreSections();
      const containers = [
        this.querySelector('.section-group__section-one-container'),
        this.querySelector('.section-group__section-two-container'),
      ];
      const sections = [
        this.resolveSection(this.dataset.sectionOneId),
        this.resolveSection(this.dataset.sectionTwoId),
      ];

      sections.forEach((section, index) => {
        const container = containers[index];
        if (!section || !container || section === this.closest('.shopify-section') || this.contains(section)) return;
        const placeholder = document.createComment(`section-group-placeholder-${index + 1}`);
        section.parentNode?.insertBefore(placeholder, section);
        container.append(section);
        section.dataset.sectionGroupManaged = 'true';
        this.moves.push({ section, placeholder });
      });

      this.classList.toggle('section-group--incomplete', this.moves.length !== 2);
      dispatch(this, 'section-group:ready', { sectionCount: this.moves.length });
    }

    restoreSections() {
      this.moves.splice(0).forEach(({ section, placeholder }) => {
        if (placeholder?.parentNode) placeholder.parentNode.insertBefore(section, placeholder);
        placeholder?.remove();
        delete section.dataset.sectionGroupManaged;
      });
    }
  }
  define('section-group', SectionGroup);
})();
